import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Grid3X3, Hand, LocateFixed, Maximize2, Plus, RotateCcw, RotateCw, ZoomIn, ZoomOut, X } from 'lucide-react';
import CapabilityTagsField from './CapabilityTagsField';
import {
  CUSTOM_CALLOUT_TYPE,
  DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR,
  DEFAULT_CUSTOM_CALLOUT_GROUP_NAME,
  DEFAULT_CALLOUT_BUBBLE_HEIGHT,
  DEFAULT_CALLOUT_BUBBLE_SCALE,
  DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH,
  DEFAULT_CALLOUT_BUBBLE_WIDTH,
  DEFAULT_CALLOUT_TEXT_SCALE,
  MAX_CALLOUT_ANCHOR_RADIUS,
  MAX_CALLOUT_BUBBLE_HEIGHT,
  MAX_CALLOUT_BUBBLE_RADIUS,
  MAX_CALLOUT_BUBBLE_STROKE_WIDTH,
  MAX_CALLOUT_BUBBLE_WIDTH,
  MAX_CALLOUT_LINE_STROKE_WIDTH,
  MAX_CALLOUT_TEXT_SCALE,
  MIN_CALLOUT_ANCHOR_RADIUS,
  MIN_CALLOUT_BUBBLE_HEIGHT,
  MIN_CALLOUT_BUBBLE_RADIUS,
  MIN_CALLOUT_BUBBLE_STROKE_WIDTH,
  MIN_CALLOUT_BUBBLE_WIDTH,
  MIN_CALLOUT_LINE_STROKE_WIDTH,
  MIN_CALLOUT_TEXT_SCALE,
  buildPinoutPreviewSvg,
  getCalloutAppearance,
  getCalloutAutoSizeMultiplier,
  getCalloutMetrics,
  getCalloutType,
  getCalloutLayoutKey,
  getCalloutRoutePoints,
  getResolvedCalloutLineAttachSide,
  getPinLayoutAliases,
  getPinLayoutKey,
  getRotatedBubbleSideMidpoint,
  doesPinMatchLayoutKey,
  getRotatedBox,
  normalizeCalloutAnchorRadius,
  normalizeCalloutBubbleHeight,
  normalizeCalloutBubbleRadius,
  normalizeCalloutBubbleScale,
  normalizeCalloutBubbleStrokeWidth,
  normalizeCalloutBubbleWidth,
  normalizeCalloutRoutePoints,
  resolveCalloutGroupColor,
  normalizeCalloutLineStrokeWidth,
  normalizeCalloutTextScale,
  normalizePinCallouts,
  parseSvgBox,
  rotatePins,
} from '../utils/pinCalloutLayout';
import {
  areSvgViewBoxesEqual,
  normalizeSvgViewBox,
} from '../utils/svgViewBox';

const EditorShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(360px, 0.95fr);
  align-items: start;
  gap: 24px;

  @media (max-width: 1220px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewPanel = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 18px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
`;

const ToolbarTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToolbarHeading = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

const ToolbarHint = styled.div`
  font-size: 13px;
  color: #475569;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: ${(props) => props.$iconOnly ? '42px' : 'auto'};
  height: ${(props) => props.$iconOnly ? '42px' : 'auto'};
  padding: ${(props) => props.$iconOnly ? '0' : '10px 14px'};
  border-radius: 10px;
  border: 1px solid ${(props) => props.$active ? '#3b82f6' : '#cbd5e1'};
  background: ${(props) => props.$active ? '#dbeafe' : 'white'};
  color: ${(props) => props.$active ? '#1d4ed8' : '#0f172a'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #1d4ed8;
    background: #eff6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PreviewViewport = styled.div`
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(191, 219, 254, 0.28), transparent 32%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
`;

const PreviewCanvas = styled.div`
  position: relative;
  height: ${(props) => props.$heightPx ? `${props.$heightPx}px` : 'clamp(320px, 56vh, 720px)'};
  border: 2px solid ${(props) => props.$dropActive ? '#3b82f6' : '#dbeafe'};
  border-radius: 14px;
  background-color: rgba(255, 255, 255, 0.92);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${(props) => props.$showGrid ? `
    background-image:
      linear-gradient(to right, rgba(15, 23, 42, 0.14) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(15, 23, 42, 0.14) 1px, transparent 1px),
      linear-gradient(to right, rgba(15, 23, 42, 0.055) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(15, 23, 42, 0.055) 1px, transparent 1px);
    background-size:
      ${props.$gridSize * 5}px ${props.$gridSize * 5}px,
      ${props.$gridSize * 5}px ${props.$gridSize * 5}px,
      ${props.$gridSize}px ${props.$gridSize}px,
      ${props.$gridSize}px ${props.$gridSize}px;
  ` : ''}

  ${(props) => props.$dropActive ? 'box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);' : ''}

  @media (max-width: 768px) {
    height: ${(props) => props.$heightPx ? `${props.$heightPx}px` : 'clamp(260px, 48vh, 520px)'};
  }
`;

const PreviewMarkup = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  min-height: inherit;
  cursor: ${(props) => props.$panMode ? 'grab' : 'crosshair'};

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const Sidebar = styled.div`
  width: 100%;
  display: grid;
  gap: 16px;

  @media (min-width: 1221px) {
    position: sticky;
    top: calc(var(--header-height, 72px) + 16px);
    max-height: calc(100vh - (var(--header-height, 72px) + 32px));
    overflow-y: auto;
    padding-right: 4px;
  }
`;

const SidebarTopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const PanelCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
  padding: 18px;
  scroll-margin-top: 24px;
`;

const PanelTitle = styled.h4`
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const PanelText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #475569;
`;

const TrayGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  max-height: 240px;
  overflow-y: auto;
`;

const LabelChip = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  padding: 11px 12px;
  border-radius: 12px;
  border: 1px ${(props) => props.$selected ? 'solid' : 'dashed'} ${(props) => {
    if (props.$selected) {
      return '#2563eb';
    }
    return props.$placed ? '#bbf7d0' : '#93c5fd';
  }};
  background: ${(props) => {
    if (props.$selected) {
      return '#dbeafe';
    }
    return props.$placed ? '#f0fdf4' : '#eff6ff';
  }};
  color: #0f172a;
  cursor: pointer;
  opacity: ${(props) => props.$placed && !props.$selected ? 0.72 : 1};
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${(props) => props.$selected ? '0 0 0 3px rgba(37, 99, 235, 0.16)' : 'none'};

  &:hover {
    transform: translateY(-1px);
    border-color: #3b82f6;
    background: ${(props) => props.$selected ? '#dbeafe' : (props.$placed ? '#dcfce7' : '#dbeafe')};
  }

  &:active {
    cursor: ${(props) => props.$placed ? 'pointer' : 'grabbing'};
  }
`;

const LabelChipTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
`;

const LabelChipMeta = styled.div`
  font-size: 11px;
  color: #475569;
  white-space: nowrap;
`;

const PinList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PinButton = styled.button`
  text-align: left;
  border: 1px solid ${(props) => props.$selected ? '#3b82f6' : '#e2e8f0'};
  background: ${(props) => props.$selected ? '#eff6ff' : '#ffffff'};
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const PinHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const PinName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const PinMeta = styled.div`
  font-size: 12px;
  color: #475569;
`;

const PlacementBadge = styled.span`
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) => props.$placed ? '#dcfce7' : '#fee2e2'};
  color: ${(props) => props.$placed ? '#166534' : '#991b1b'};
`;

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  grid-column: ${(props) => props.$fullWidth ? '1 / -1' : 'auto'};
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FieldInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`;

const FieldTextArea = styled.textarea`
  width: 100%;
  min-width: 0;
  min-height: 88px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  font-family: inherit;
  color: #0f172a;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
  }
`;

const InlineFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const RangeFieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px;
  gap: 12px;
  align-items: center;
`;

const RangeInput = styled.input`
  width: 100%;
  min-width: 0;
  margin: 0;
  accent-color: #2563eb;
`;

const MiniActions = styled.div`
  display: flex;
  justify-content: ${(props) => props.$center ? 'center' : 'flex-start'};
  gap: 10px;
  flex-wrap: ${(props) => props.$singleRow ? 'nowrap' : 'wrap'};
  overflow-x: ${(props) => props.$singleRow ? 'auto' : 'visible'};
`;

const MiniButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(props) => {
    if (props.$danger) {
      return '#fecaca';
    }
    if (props.$active) {
      return '#93c5fd';
    }
    return '#cbd5e1';
  }};
  background: ${(props) => {
    if (props.$danger) {
      return '#fef2f2';
    }
    if (props.$active) {
      return '#dbeafe';
    }
    return '#ffffff';
  }};
  color: ${(props) => {
    if (props.$danger) {
      return '#b91c1c';
    }
    if (props.$active) {
      return '#1d4ed8';
    }
    return '#0f172a';
  }};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
`;

const CompactButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #1d4ed8;
    background: #eff6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToolbarNumberInput = styled.input`
  width: 72px;
  height: 42px;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`;

const EmptyState = styled.div`
  padding: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  font-size: 13px;
  color: #64748b;
  text-align: center;
`;

const pinDisplayName = (pin) => String(pin?.pin_name || pin?.pinName || pin?.pin_number || pin?.pinNumber || 'Pin');

const pinGroupName = (pin) => String(pin?.group_name || 'Other');
const calloutGroupName = (callout) => String(
  callout?.group_name || callout?.groupName || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME
);

const customCalloutDisplayName = (callout, index = 0) => String(callout?.label || `Custom Label ${index + 1}`);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const areStringArraysEqual = (first = [], second = []) => (
  first.length === second.length && first.every((value, index) => value === second[index])
);
const CUSTOM_CALLOUT_PIN_ROLE_OPTIONS = [
  { value: '', label: 'Select role...' },
  { value: 'power', label: 'Power' },
  { value: 'ground', label: 'Ground' },
  { value: 'input', label: 'Input' },
  { value: 'output', label: 'Output' },
  { value: 'io', label: 'I/O' },
  { value: 'analog', label: 'Analog' },
  { value: 'clock', label: 'Clock' },
  { value: 'reset', label: 'Reset' },
  { value: 'control', label: 'Control' },
  { value: 'communication', label: 'Communication' },
  { value: 'nc', label: 'No Connect' },
];
const CUSTOM_CALLOUT_SUGGESTED_CAPABILITY_TAGS = [
  'gpio',
  'digital',
  'pio',
  'digital-input',
  'digital-output',
  'analog-input',
  'pwm',
  'interrupt',
  'i2c-sda',
  'i2c-scl',
  'spi-mosi',
  'spi-miso',
  'spi-sck',
  'spi-cs',
  'uart-tx',
  'uart-rx',
  'power',
  'ground',
  'control',
  'reset',
  'clock',
  'enable',
  'active-low',
  'active-high',
  'open-drain',
  'tri-state',
];
const getNumericSvgAttributeFromMarkup = (svgMarkup, attributeName) => {
  const escapedAttributeName = String(attributeName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b${escapedAttributeName}=["']([^"']+)["']`, 'i');
  const numericValue = Number.parseFloat(String(svgMarkup || '').match(pattern)?.[1] || '');
  return Number.isFinite(numericValue) ? numericValue : NaN;
};

const toCoordinate = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }

  return Number(numeric.toFixed(2));
};

const snapValueToGrid = (value, size) => {
  const numericValue = Number(value);
  const numericSize = Number(size);

  if (!Number.isFinite(numericValue) || !Number.isFinite(numericSize) || numericSize <= 0) {
    return numericValue;
  }

  return Number((Math.round(numericValue / numericSize) * numericSize).toFixed(2));
};

const snapPointToGrid = (point, size) => {
  if (!point) {
    return point;
  }

  return {
    x: snapValueToGrid(point.x, size),
    y: snapValueToGrid(point.y, size),
  };
};

const getSvgPointFromClientCoordinates = (container, clientX, clientY) => {
  const svg = container?.querySelector('svg');
  if (!svg || typeof svg.createSVGPoint !== 'function') {
    return null;
  }

  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const matrix = svg.getScreenCTM();
  if (!matrix) {
    return null;
  }

  return point.matrixTransform(matrix.inverse());
};

const getSvgPoint = (container) => (event) => {
  return getSvgPointFromClientCoordinates(container, event.clientX, event.clientY);
};

const PREVIEW_INTERACTIVE_SELECTOR = [
  '.pin-hole',
  '.pin-rect',
  '.pin-callout-bubble',
  '.pin-callout-line',
  '.pin-callout-line-hit',
  '.pin-callout-anchor',
  '.pin-callout-anchor-hit',
  '.pin-callout-route-point',
  '.pin-callout-route-point-hit',
].join(', ');

const getInteractivePinKey = (element) => {
  if (!(element instanceof Element)) {
    return '';
  }

  const calloutElement = element.closest('[data-callout-key]');
  const calloutKey = String(calloutElement?.getAttribute('data-callout-key') || '').trim();

  if (calloutKey) {
    return calloutKey;
  }

  const svgId = String(element.getAttribute('data-svgid') || '').trim();
  if (svgId) {
    return `svg:${svgId}`;
  }

  const pinId = String(element.getAttribute('data-pin-id') || '').trim();
  if (pinId) {
    return `id:${pinId}`;
  }

  const pinNumber = String(element.getAttribute('data-pin-number') || '').trim();
  if (pinNumber) {
    return `num:${pinNumber}`;
  }

  return `name:${String(element.getAttribute('data-pin') || '').trim()}`;
};

const getPinValue = (pin, keys) => {
  for (const key of keys) {
    const value = pin?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  return '';
};

const doesElementMatchPin = (element, pin) => {
  if (!(element instanceof Element) || !pin) {
    return false;
  }

  const elementPinId = String(element.getAttribute('data-pin-id') || '').trim();
  const pinId = getPinValue(pin, ['id', 'pin_id', 'pinId']);
  if (elementPinId && pinId && elementPinId === pinId) {
    return true;
  }

  const elementSvgId = String(element.getAttribute('data-svgid') || '').trim();
  const pinSvgId = getPinValue(pin, ['svg_id', 'svgId']);
  if (elementSvgId && pinSvgId && elementSvgId === pinSvgId) {
    return true;
  }

  const elementPinNumber = String(element.getAttribute('data-pin-number') || '').trim();
  const pinNumber = getPinValue(pin, ['pin_number', 'pinNumber']);
  if (elementPinNumber && pinNumber && elementPinNumber === pinNumber) {
    return true;
  }

  const elementPinName = String(element.getAttribute('data-pin') || '').trim().toLowerCase();
  const pinName = getPinValue(pin, ['pin_name', 'pinName']).toLowerCase();
  if (elementPinName && pinName && elementPinName === pinName) {
    return true;
  }

  return false;
};

const findPinForElement = (pins, element) => {
  if (!(element instanceof Element)) {
    return null;
  }

  return (Array.isArray(pins) ? pins : []).find((pin) => doesElementMatchPin(element, pin)) || null;
};

const getSvgElementCenter = (element, previewContainer) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return null;
  }

  try {
    const box = element.getBoundingClientRect();
    if (!Number.isFinite(box.left) || !Number.isFinite(box.top) || box.width <= 0 || box.height <= 0) {
      return null;
    }

    return getSvgPointFromClientCoordinates(
      previewContainer,
      box.left + (box.width / 2),
      box.top + (box.height / 2)
    );
  } catch (error) {
    return null;
  }
};

const findBoardPinSnapTarget = (event, previewContainer, pins) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const stackedElements = document.elementsFromPoint(event.clientX, event.clientY);
  const boardPinElement = stackedElements.find((candidate) => {
    if (!(candidate instanceof Element)) {
      return false;
    }

    if (!previewContainer?.contains(candidate)) {
      return false;
    }

    if (!candidate.matches('.pin-hole, .pin-rect')) {
      return false;
    }

    return candidate.getAttribute('data-micropinouts-callout') !== '1';
  });

  if (!boardPinElement) {
    return null;
  }

  const pin = findPinForElement(pins, boardPinElement);
  if (!pin) {
    return null;
  }

  const elementCenter = getSvgElementCenter(boardPinElement, previewContainer);
  return {
    pin,
    point: elementCenter || {
      x: Number(pin.position_x),
      y: Number(pin.position_y),
    },
  };
};

const getDefaultAnchorPointForLabel = (point, boardBox) => {
  const centerX = Number(boardBox?.minX || 0) + (Number(boardBox?.width || 0) / 2);
  const centerY = Number(boardBox?.minY || 0) + (Number(boardBox?.height || 0) / 2);
  const deltaX = centerX - point.x;
  const deltaY = centerY - point.y;
  const gap = 48;

  if (Math.abs(deltaX) >= Math.abs(deltaY)) {
    return {
      x: point.x + (deltaX >= 0 ? gap : -gap),
      y: point.y,
    };
  }

  return {
    x: point.x,
    y: point.y + (deltaY >= 0 ? gap : -gap),
  };
};

const getDefaultPinAnchorPoint = (pin, fallbackPoint, boardBox) => {
  const pinX = Number(pin?.position_x ?? pin?.position?.x);
  const pinY = Number(pin?.position_y ?? pin?.position?.y);

  if (Number.isFinite(pinX) && Number.isFinite(pinY)) {
    return {
      x: Number(pinX.toFixed(2)),
      y: Number(pinY.toFixed(2)),
    };
  }

  return getDefaultAnchorPointForLabel(fallbackPoint, boardBox);
};

const getCalloutVisualFields = (callout = {}) => {
  const appearance = getCalloutAppearance(callout);
  return {
    text_scale: appearance.textScale,
    bubble_scale: appearance.bubbleScale,
    label_scale: appearance.bubbleScale,
    bubble_width: appearance.bubbleWidth,
    bubble_height: appearance.bubbleHeight,
    bubble_radius: appearance.bubbleRadius,
    bubble_stroke_width: appearance.bubbleStrokeWidth,
    line_stroke_width: appearance.lineStrokeWidth,
    anchor_radius: appearance.anchorRadius,
  };
};

const getCalloutStyleTemplate = (callout = {}) => {
  const visualFields = getCalloutVisualFields(callout);

  return {
    ...visualFields,
    rotation_deg: Number(callout.rotation_deg || 0),
    line_attach_side: callout.line_attach_side || 'auto',
    line_style: callout.line_style || 'orthogonal',
  };
};

const DEFAULT_EDITOR_CALLOUT_REFERENCE_LONG_SIDE = 400;
const DEFAULT_EDITOR_CALLOUT_MAX_MULTIPLIER = 5.2;

const buildDefaultFutureCalloutStyle = ({ boardBox, referenceViewScale = 1 } = {}) => {
  const boardMultiplier = getCalloutAutoSizeMultiplier(boardBox, {
    calloutReferenceLongSide: DEFAULT_EDITOR_CALLOUT_REFERENCE_LONG_SIDE,
    calloutAutoSizeMax: DEFAULT_EDITOR_CALLOUT_MAX_MULTIPLIER,
  });
  const displayMultiplier = Number.isFinite(Number(referenceViewScale))
    ? Math.max(1, Math.min(DEFAULT_EDITOR_CALLOUT_MAX_MULTIPLIER, Number(referenceViewScale)))
    : 1;
  const sizeMultiplier = Math.max(1, Math.min(
    DEFAULT_EDITOR_CALLOUT_MAX_MULTIPLIER,
    Math.max(boardMultiplier, displayMultiplier)
  ));
  const baseScale = normalizeCalloutBubbleScale(DEFAULT_CALLOUT_BUBBLE_SCALE * sizeMultiplier);

  const strokeMultiplier = Math.max(1, Math.min(3.2, sizeMultiplier));
  const anchorMultiplier = Math.max(1, Math.min(3.8, sizeMultiplier));


  return getCalloutStyleTemplate({
    text_scale: normalizeCalloutTextScale(DEFAULT_CALLOUT_TEXT_SCALE * sizeMultiplier),
    bubble_scale: baseScale,
    label_scale: baseScale,
    bubble_width: baseScale,
    bubble_height: baseScale,
    bubble_stroke_width: normalizeCalloutBubbleStrokeWidth(
      DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH * strokeMultiplier
    ),

    line_stroke_width: normalizeCalloutLineStrokeWidth(
      DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH * strokeMultiplier
    ),

    anchor_radius: normalizeCalloutAnchorRadius(
      2.4 * anchorMultiplier
    ),
  });
};

const getCalloutBoxMetrics = (label, callout = {}) => {
  const metrics = getCalloutMetrics(label, callout);
  return {
    ...metrics,
    text_scale: metrics.textScale,
    bubble_scale: metrics.bubbleScale,
    label_scale: metrics.bubbleScale,
    bubble_width: metrics.bubbleWidth,
    bubble_height: metrics.bubbleHeight,
  };
};

const normalizeCalloutBubbleDimension = (dimensionKey, value, fallback) => (
  dimensionKey === 'bubble_height'
    ? normalizeCalloutBubbleHeight(value, fallback)
    : normalizeCalloutBubbleWidth(value, fallback)
);

const getCalloutLabelText = (callout) => String(
  callout?.label ||
  callout?.pin_name ||
  callout?.pinName ||
  callout?.pin_number ||
  callout?.pinNumber ||
  'Label'
);

const getCalloutRect = (callout) => {
  if (!callout) {
    return null;
  }

  const left = Number(callout.label_x);
  const top = Number(callout.label_y);
  if (!Number.isFinite(left) || !Number.isFinite(top)) {
    return null;
  }

  const metrics = getCalloutBoxMetrics(getCalloutLabelText(callout), callout);
  return {
    left,
    top,
    right: left + metrics.width,
    bottom: top + metrics.height,
    width: metrics.width,
    height: metrics.height,
  };
};

const getCalloutLeaderGeometry = (callout, fallbackAnchorPoint = null) => {
  if (!callout) {
    return null;
  }

  const labelX = Number(callout.label_x);
  const labelY = Number(callout.label_y);
  const anchorX = Number.isFinite(Number(callout.anchor_x))
    ? Number(callout.anchor_x)
    : Number(fallbackAnchorPoint?.x);
  const anchorY = Number.isFinite(Number(callout.anchor_y))
    ? Number(callout.anchor_y)
    : Number(fallbackAnchorPoint?.y);

  if (
    !Number.isFinite(labelX)
    || !Number.isFinite(labelY)
    || !Number.isFinite(anchorX)
    || !Number.isFinite(anchorY)
  ) {
    return null;
  }

  const label = getCalloutLabelText(callout);
  const metrics = getCalloutBoxMetrics(label, callout);
  const labelCenterX = labelX + (metrics.width / 2);
  const labelCenterY = labelY + (metrics.height / 2);
  const routePoints = getCalloutRoutePoints(callout);
  const lineAttachSide = getResolvedCalloutLineAttachSide({
    lineAttachSide: callout?.line_attach_side,
    labelCenterX,
    labelCenterY,
    anchorX,
    anchorY,
    routePoints,
  });
  const entryPoint = getRotatedBubbleSideMidpoint({
    centerX: labelCenterX,
    centerY: labelCenterY,
    width: metrics.width,
    height: metrics.height,
    rotationDeg: Number(callout?.rotation_deg) || 0,
    side: lineAttachSide,
  });

  return {
    anchorPoint: {
      x: anchorX,
      y: anchorY,
    },
    entryPoint: {
      x: Number(entryPoint.x.toFixed(2)),
      y: Number(entryPoint.y.toFixed(2)),
    },
    lineAttachSide,
    routePoints,
  };
};

const getDistancePointToSegment = (point, start, end) => {
  if (!point || !start || !end) {
    return Infinity;
  }

  const dx = end.x - start.x;
  const dy = end.y - start.y;

  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      (((point.x - start.x) * dx) + ((point.y - start.y) * dy)) / ((dx * dx) + (dy * dy))
    )
  );

  const projected = {
    x: start.x + (t * dx),
    y: start.y + (t * dy),
  };

  return Math.hypot(point.x - projected.x, point.y - projected.y);
};

const getClosestCalloutRouteInsertIndex = (callout, clickPoint, fallbackAnchorPoint = null) => {
  const geometry = getCalloutLeaderGeometry(callout, fallbackAnchorPoint);

  if (!geometry || !clickPoint) {
    return getCalloutRoutePoints(callout).length;
  }

  const points = [geometry.entryPoint, ...geometry.routePoints, geometry.anchorPoint];
  let bestSegmentIndex = 0;
  let bestDistance = Infinity;

  for (let index = 0; index < points.length - 1; index += 1) {
    const distance = getDistancePointToSegment(clickPoint, points[index], points[index + 1]);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestSegmentIndex = index;
    }
  }

  return bestSegmentIndex;
};

const getAnchorCollisionRect = (point, radius) => {
  if (!point) {
    return null;
  }

  const safeRadius = Math.max(1, Number(radius) || 1);
  return {
    left: point.x - safeRadius,
    top: point.y - safeRadius,
    right: point.x + safeRadius,
    bottom: point.y + safeRadius,
    width: safeRadius * 2,
    height: safeRadius * 2,
  };
};

const applyVisualPartialToCallout = (callout, partial) => {
  if (!callout || !partial || Object.keys(partial).length === 0) {
    return callout;
  }

  const nextCallout = {
    ...callout,
    ...partial,
  };
  const affectsCalloutSize = hasOwn(partial, 'text_scale')
    || hasOwn(partial, 'bubble_scale')
    || hasOwn(partial, 'label_scale')
    || hasOwn(partial, 'bubble_width')
    || hasOwn(partial, 'bubble_height');

  if (!affectsCalloutSize) {
    return nextCallout;
  }

  const currentLabelX = Number(callout.label_x);
  const currentLabelY = Number(callout.label_y);
  if (!Number.isFinite(currentLabelX) || !Number.isFinite(currentLabelY)) {
    return nextCallout;
  }

  const label = getCalloutLabelText(callout);
  const currentMetrics = getCalloutBoxMetrics(label, callout);
  const nextMetrics = getCalloutBoxMetrics(label, nextCallout);

  return {
    ...nextCallout,
    label_x: Number((currentLabelX + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
    label_y: Number((currentLabelY + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
  };
};

const findCalloutForPinInList = (callouts, pin) => {
  if (!pin) {
    return null;
  }

  const aliases = new Set(getPinLayoutAliases(pin));
  return (Array.isArray(callouts) ? callouts : []).find((callout) => aliases.has(getCalloutLayoutKey(callout))) || null;
};

const INITIAL_FRAME_PADDING_RATIO = 0.28;
const MIN_FRAME_PADDING = 18;
const CUSTOM_CALLOUT_STACK_OFFSET = 22;
const REFERENCE_VIEW_SCALE_STEP = 1.15;
const AUTO_PLACE_CANVAS_PADDING = 24;
const AUTO_PLACE_ROW_GAP = 14;
const AUTO_PLACE_COLUMN_GAP = 18;

const buildCenteredScaledViewBox = (viewBox, scale = 1) => {
  const normalizedViewBox = normalizeSvgViewBox(viewBox);
  const numericScale = Number(scale);

  if (!normalizedViewBox || !Number.isFinite(numericScale) || numericScale <= 0) {
    return normalizedViewBox;
  }

  const centerX = normalizedViewBox.minX + (normalizedViewBox.width / 2);
  const centerY = normalizedViewBox.minY + (normalizedViewBox.height / 2);
  const nextWidth = normalizedViewBox.width / numericScale;
  const nextHeight = normalizedViewBox.height / numericScale;

  return normalizeSvgViewBox({
    minX: centerX - (nextWidth / 2),
    minY: centerY - (nextHeight / 2),
    width: nextWidth,
    height: nextHeight,
  });
};

const buildPaddedViewBox = (viewBox, paddingRatio = INITIAL_FRAME_PADDING_RATIO, minPadding = MIN_FRAME_PADDING) => {
  const normalizedViewBox = normalizeSvgViewBox(viewBox);
  if (!normalizedViewBox) {
    return null;
  }

  const framePadding = Math.max(
    minPadding,
    Math.max(normalizedViewBox.width, normalizedViewBox.height) * paddingRatio
  );

  return normalizeSvgViewBox({
    minX: normalizedViewBox.minX - framePadding,
    minY: normalizedViewBox.minY - framePadding,
    width: normalizedViewBox.width + (framePadding * 2),
    height: normalizedViewBox.height + (framePadding * 2),
  });
};

const translateViewBox = (viewBox, deltaX = 0, deltaY = 0) => {
  const normalizedViewBox = normalizeSvgViewBox(viewBox);
  if (!normalizedViewBox) {
    return null;
  }

  return normalizeSvgViewBox({
    minX: normalizedViewBox.minX + deltaX,
    minY: normalizedViewBox.minY + deltaY,
    width: normalizedViewBox.width,
    height: normalizedViewBox.height,
  });
};

const PinoutCalloutEditor = ({
  svgContent,
  pins,
  pinGroups = [],
  rotation,
  callouts,
  onRotationChange,
  onCalloutsChange,
  disabled = false,
  selectedPinKey: controlledSelectedPinKey,
  onSelectedPinKeyChange,
  onNormalizeCanvasSize,
  referenceSvgContent = '',
  referenceDisplayScale = 1,
  initialViewBox = null,
  onViewBoxChange,
}) => {
  const previewRef = useRef(null);
  const editorViewBoxRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const [uncontrolledSelectedPinKey, setUncontrolledSelectedPinKey] = useState('');
  const [selectedTrayPinKeys, setSelectedTrayPinKeys] = useState([]);
  const [additionalSelectedCalloutKeys, setAdditionalSelectedCalloutKeys] = useState([]);
  const [dragState, setDragState] = useState(null);
  const [draggedTrayPinKey, setDraggedTrayPinKey] = useState('');
  const [isDropActive, setIsDropActive] = useState(false);
  const [panModeEnabled, setPanModeEnabled] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(10);
  const [editorViewBox, setEditorViewBox] = useState(null);
  const [applyVisualChangesToAll, setApplyVisualChangesToAll] = useState(false);
  const [applyVisualChangesToAllExisting, setApplyVisualChangesToAllExisting] = useState(false);
  const [futureCalloutStyle, setFutureCalloutStyle] = useState(() => getCalloutStyleTemplate({
    text_scale: DEFAULT_CALLOUT_TEXT_SCALE,
    bubble_scale: DEFAULT_CALLOUT_BUBBLE_SCALE,
    bubble_stroke_width: DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH,
  }));
  const selectedPinKey = controlledSelectedPinKey ?? uncontrolledSelectedPinKey;
  const setSelectedPinKey = useCallback((nextKey) => {
    if (controlledSelectedPinKey === undefined) {
      setUncontrolledSelectedPinKey(nextKey);
    }

    if (typeof onSelectedPinKeyChange === 'function') {
      onSelectedPinKeyChange(nextKey);
    }
  }, [controlledSelectedPinKey, onSelectedPinKeyChange]);
  const lockedPreviewBox = ['anchor', 'label', 'route-point'].includes(dragState?.mode)
    ? (dragState.viewBoxLock || null)
    : null;
  const normalizedCallouts = useMemo(() => normalizePinCallouts(callouts), [callouts]);
  const calloutKeySet = useMemo(
    () => new Set(normalizedCallouts.map((callout) => getCalloutLayoutKey(callout))),
    [normalizedCallouts]
  );
  const rotatedPins = useMemo(() => rotatePins(pins, svgContent, rotation), [pins, rotation, svgContent]);
  const rotatedBoardBox = useMemo(
    () => getRotatedBox(parseSvgBox(svgContent), rotation),
    [rotation, svgContent]
  );
  const rotatedBoardLongSide = useMemo(
    () => Math.max(Number(rotatedBoardBox?.width) || 0, Number(rotatedBoardBox?.height) || 0),
    [rotatedBoardBox]
  );
  const referencePreviewBox = useMemo(() => {
    const trimmedReferenceSvgContent = String(referenceSvgContent || '').trim();
    if (!trimmedReferenceSvgContent) {
      return null;
    }

    const parsedReferenceBox = parseSvgBox(trimmedReferenceSvgContent);
    if (!Number.isFinite(Number(parsedReferenceBox?.width)) || Number(parsedReferenceBox.width) <= 0) {
      return null;
    }

    if (!Number.isFinite(Number(parsedReferenceBox?.height)) || Number(parsedReferenceBox.height) <= 0) {
      return null;
    }

    return parsedReferenceBox;
  }, [referenceSvgContent]);
  const normalizedReferenceDisplayScale = useMemo(() => {
    const numericDisplayScale = Number(referenceDisplayScale);
    if (!Number.isFinite(numericDisplayScale)) {
      return 1;
    }

    return Math.min(3, Math.max(0.25, numericDisplayScale));
  }, [referenceDisplayScale]);
  const referenceBaseViewScale = useMemo(() => {
    if (!referencePreviewBox) {
      return 1;
    }

    const originalWidth = getNumericSvgAttributeFromMarkup(referenceSvgContent, 'data-orig-vbw');
    const originalHeight = getNumericSvgAttributeFromMarkup(referenceSvgContent, 'data-orig-vbh');
    const widthScale = Number.isFinite(originalWidth) && originalWidth > 0
      ? referencePreviewBox.width / originalWidth
      : NaN;
    const heightScale = Number.isFinite(originalHeight) && originalHeight > 0
      ? referencePreviewBox.height / originalHeight
      : NaN;
    const boardFitScale = Number.isFinite(widthScale) && Number.isFinite(heightScale)
      ? Math.min(widthScale, heightScale)
      : (Number.isFinite(widthScale) ? widthScale : (Number.isFinite(heightScale) ? heightScale : 1));

    return Number((boardFitScale * normalizedReferenceDisplayScale).toFixed(4));
  }, [normalizedReferenceDisplayScale, referencePreviewBox, referenceSvgContent]);
  const defaultFutureCalloutStyle = useMemo(
    () => buildDefaultFutureCalloutStyle({
      boardBox: {
        width: rotatedBoardLongSide,
        height: rotatedBoardLongSide,
      },
      referenceViewScale: referenceBaseViewScale,
    }),
    [referenceBaseViewScale, rotatedBoardLongSide]
  );
  const defaultEditorViewBox = useMemo(() => {
    const normalizedInitialViewBox = normalizeSvgViewBox(initialViewBox);
    if (normalizedInitialViewBox) {
      return normalizedInitialViewBox;
    }

    if (referencePreviewBox) {
      return buildCenteredScaledViewBox(referencePreviewBox, referenceBaseViewScale);
    }

    return buildPaddedViewBox(rotatedBoardBox);
  }, [initialViewBox, referenceBaseViewScale, referencePreviewBox, rotatedBoardBox]);

  useEffect(() => {
    setFutureCalloutStyle(defaultFutureCalloutStyle);
  }, [defaultFutureCalloutStyle, referenceDisplayScale, referenceSvgContent, svgContent]);

  useEffect(() => {
    editorViewBoxRef.current = editorViewBox;
  }, [editorViewBox]);

  const commitEditorViewBox = useCallback((nextViewBoxOrUpdater) => {
    const currentViewBox = editorViewBoxRef.current || defaultEditorViewBox;
    const rawNextViewBox = typeof nextViewBoxOrUpdater === 'function'
      ? nextViewBoxOrUpdater(currentViewBox)
      : nextViewBoxOrUpdater;
    const normalizedNextViewBox = normalizeSvgViewBox(rawNextViewBox);

    if (!normalizedNextViewBox || areSvgViewBoxesEqual(currentViewBox, normalizedNextViewBox)) {
      return;
    }

    editorViewBoxRef.current = normalizedNextViewBox;
    setEditorViewBox(normalizedNextViewBox);

    if (typeof onViewBoxChange === 'function' && !areSvgViewBoxesEqual(initialViewBox, normalizedNextViewBox)) {
      onViewBoxChange(normalizedNextViewBox);
    }
  }, [defaultEditorViewBox, initialViewBox, onViewBoxChange]);
  const setEditorViewBoxLocally = useCallback((nextViewBoxOrUpdater) => {
    const currentViewBox = editorViewBoxRef.current || defaultEditorViewBox;
    const rawNextViewBox = typeof nextViewBoxOrUpdater === 'function'
      ? nextViewBoxOrUpdater(currentViewBox)
      : nextViewBoxOrUpdater;
    const normalizedNextViewBox = normalizeSvgViewBox(rawNextViewBox);

    if (!normalizedNextViewBox || areSvgViewBoxesEqual(currentViewBox, normalizedNextViewBox)) {
      return;
    }

    editorViewBoxRef.current = normalizedNextViewBox;
    setEditorViewBox(normalizedNextViewBox);
  }, [defaultEditorViewBox]);

  useEffect(() => {
    if (areSvgViewBoxesEqual(editorViewBoxRef.current, defaultEditorViewBox)) {
      return;
    }

    editorViewBoxRef.current = defaultEditorViewBox;
    setEditorViewBox(defaultEditorViewBox);
  }, [defaultEditorViewBox]);
  const selectedPreviewCalloutKey = useMemo(() => {
    if (!selectedPinKey) {
      return '';
    }

    const directMatch = normalizedCallouts.find((callout) => getCalloutLayoutKey(callout) === selectedPinKey);
    if (directMatch) {
      return getCalloutLayoutKey(directMatch);
    }

    const matchedPin = rotatedPins.find((pin) => doesPinMatchLayoutKey(pin, selectedPinKey));
    if (!matchedPin) {
      return '';
    }

    const matchedCallout = findCalloutForPinInList(normalizedCallouts, matchedPin);
    return matchedCallout ? getCalloutLayoutKey(matchedCallout) : '';
  }, [normalizedCallouts, rotatedPins, selectedPinKey]);
  const selectedPreviewCalloutKeys = useMemo(() => {
    if (!selectedPreviewCalloutKey) {
      return [];
    }

    const nextKeys = [selectedPreviewCalloutKey];
    additionalSelectedCalloutKeys.forEach((key) => {
      if (!key || key === selectedPreviewCalloutKey || !calloutKeySet.has(key)) {
        return;
      }

      nextKeys.push(key);
    });
    return nextKeys;
  }, [additionalSelectedCalloutKeys, calloutKeySet, selectedPreviewCalloutKey]);
  const selectedPreviewCalloutKeySet = useMemo(
    () => new Set(selectedPreviewCalloutKeys),
    [selectedPreviewCalloutKeys]
  );

  const previewSvg = useMemo(
    () => buildPinoutPreviewSvg(svgContent, pins, normalizedCallouts, rotation, {
      viewBoundsMode: 'board',
      minFramePadding: MIN_FRAME_PADDING,
      lockedViewBox: lockedPreviewBox || editorViewBox || defaultEditorViewBox || null,
      normalizeCalloutSize: false,
      showEditorRouteHandles: true,
      selectedCalloutKey: selectedPreviewCalloutKey,
      selectedCalloutKeys: selectedPreviewCalloutKeys,
    }),
    [
      defaultEditorViewBox,
      editorViewBox,
      selectedPreviewCalloutKey,
      selectedPreviewCalloutKeys,
      svgContent,
      pins,
      normalizedCallouts,
      rotation,
      lockedPreviewBox,
    ]
  );
  const previewViewBox = useMemo(
    () => normalizeSvgViewBox(lockedPreviewBox || editorViewBox || defaultEditorViewBox || rotatedBoardBox),
    [defaultEditorViewBox, editorViewBox, lockedPreviewBox, rotatedBoardBox]
  );
  const previewAspectRatio = useMemo(() => {
    const boxWidth = Number(previewViewBox?.width);
    const boxHeight = Number(previewViewBox?.height);

    if (!Number.isFinite(boxWidth) || boxWidth <= 0 || !Number.isFinite(boxHeight) || boxHeight <= 0) {
      return null;
    }

    return boxWidth / boxHeight;
  }, [previewViewBox]);
  const previewCanvasHeight = useMemo(() => {
    if (!Number.isFinite(previewAspectRatio) || previewAspectRatio <= 0 || previewWidth <= 0) {
      return null;
    }

    const isCompactLayout = previewWidth <= 768;
    const rawHeight = previewWidth / previewAspectRatio;
    const viewportMaxHeight = typeof window !== 'undefined'
      ? window.innerHeight * (isCompactLayout ? 0.68 : 0.74)
      : 760;
    const minHeight = isCompactLayout ? 240 : 280;
    const maxHeight = Math.min(isCompactLayout ? 560 : 760, viewportMaxHeight);

    return Math.round(Math.max(minHeight, Math.min(maxHeight, rawHeight)));
  }, [previewAspectRatio, previewWidth]);
  useEffect(() => {
    const element = previewRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateWidth = (nextWidth) => {
      const roundedWidth = Math.max(0, Math.round(Number(nextWidth) || 0));
      setPreviewWidth((currentWidth) => (currentWidth === roundedWidth ? currentWidth : roundedWidth));
    };

    updateWidth(element.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entryWidth = entries?.[0]?.contentRect?.width;
      updateWidth(entryWidth ?? element.getBoundingClientRect().width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const calloutMap = useMemo(
    () => new Map(normalizedCallouts.map((callout) => [getCalloutLayoutKey(callout), callout])),
    [normalizedCallouts]
  );
  const customCallouts = useMemo(
    () => normalizedCallouts.filter((callout) => getCalloutType(callout) === CUSTOM_CALLOUT_TYPE),
    [normalizedCallouts]
  );
  const customCalloutCategoryOptions = useMemo(() => {
    const options = [];
    const optionIndexByKey = new Map();

    const addOption = (name, color) => {
      const normalizedName = String(name || '').trim();
      if (!normalizedName) {
        return;
      }

      const optionKey = normalizedName.toLowerCase();
      const resolvedColor = resolveCalloutGroupColor(normalizedName, color);
      const existingIndex = optionIndexByKey.get(optionKey);

      if (existingIndex !== undefined) {
        if (!options[existingIndex].color && resolvedColor) {
          options[existingIndex] = {
            ...options[existingIndex],
            color: resolvedColor,
          };
        }
        return;
      }

      optionIndexByKey.set(optionKey, options.length);
      options.push({
        value: normalizedName,
        label: normalizedName,
        color: resolvedColor,
      });
    };

    addOption(DEFAULT_CUSTOM_CALLOUT_GROUP_NAME, DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR);

    (Array.isArray(pinGroups) ? pinGroups : []).forEach((group) => {
      addOption(group?.name ?? group?.group_name, group?.color ?? group?.group_color);
    });

    (Array.isArray(pins) ? pins : []).forEach((pin) => {
      addOption(pin?.group_name ?? pin?.groupName, pin?.group_color ?? pin?.groupColor);
    });

    customCallouts.forEach((callout) => {
      addOption(callout?.group_name ?? callout?.groupName, callout?.group_color ?? callout?.groupColor);
    });

    return options;
  }, [customCallouts, pinGroups, pins]);
  const customCalloutCategoryOptionMap = useMemo(
    () => new Map(customCalloutCategoryOptions.map((option) => [option.value.toLowerCase(), option])),
    [customCalloutCategoryOptions]
  );
  const getCalloutForPin = useCallback(
    (pin) => findCalloutForPinInList(normalizedCallouts, pin),
    [normalizedCallouts]
  );
  const unplacedPins = useMemo(
    () => rotatedPins.filter((pin) => !findCalloutForPinInList(normalizedCallouts, pin)),
    [normalizedCallouts, rotatedPins]
  );
  const selectedTrayPinKeySet = useMemo(
    () => new Set(selectedTrayPinKeys),
    [selectedTrayPinKeys]
  );
  const selectedTrayPins = useMemo(
    () => unplacedPins.filter((pin) => selectedTrayPinKeySet.has(getPinLayoutKey(pin))),
    [selectedTrayPinKeySet, unplacedPins]
  );

  useEffect(() => {
    const visibleKeySet = new Set(unplacedPins.map((pin) => getPinLayoutKey(pin)));
    setSelectedTrayPinKeys((currentKeys) => {
      const nextKeys = currentKeys.filter((key) => visibleKeySet.has(key));
      return nextKeys.length === currentKeys.length ? currentKeys : nextKeys;
    });
  }, [unplacedPins]);

  useEffect(() => {
    setAdditionalSelectedCalloutKeys((currentKeys) => {
      if (!selectedPreviewCalloutKey) {
        return currentKeys.length === 0 ? currentKeys : [];
      }

      const nextKeys = currentKeys.filter(
        (key) => key !== selectedPreviewCalloutKey && calloutKeySet.has(key)
      );
      return areStringArraysEqual(currentKeys, nextKeys) ? currentKeys : nextKeys;
    });
  }, [calloutKeySet, selectedPreviewCalloutKey]);

  useEffect(() => {
    if (selectedPinKey && (calloutMap.has(selectedPinKey) || rotatedPins.some((pin) => doesPinMatchLayoutKey(pin, selectedPinKey)))) {
      return;
    }

    if (rotatedPins.length > 0) {
      setSelectedPinKey(getPinLayoutKey(rotatedPins[0]));
      return;
    }

    if (customCallouts.length > 0) {
      setSelectedPinKey(getCalloutLayoutKey(customCallouts[0]));
      return;
    }

    if (selectedPinKey) {
      setSelectedPinKey('');
    }
  }, [calloutMap, customCallouts, rotatedPins, selectedPinKey, setSelectedPinKey]);

  const selectedPin = rotatedPins.find((pin) => doesPinMatchLayoutKey(pin, selectedPinKey)) || null;
  const selectedCallout =
    calloutMap.get(selectedPinKey) ||
    (selectedPin ? getCalloutForPin(selectedPin) : null) ||
    null;
  const selectedCalloutKey = selectedCallout ? getCalloutLayoutKey(selectedCallout) : '';
  const selectedCustomCallout = !selectedPin && selectedCallout && getCalloutType(selectedCallout) === CUSTOM_CALLOUT_TYPE
    ? selectedCallout
    : null;
  const selectedCustomCalloutCategory = useMemo(() => {
    if (!selectedCustomCallout) {
      return null;
    }

    const groupName = calloutGroupName(selectedCustomCallout).trim() || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME;
    return customCalloutCategoryOptionMap.get(groupName.toLowerCase()) || {
      value: groupName,
      label: groupName,
      color: resolveCalloutGroupColor(
        groupName,
        selectedCustomCallout?.group_color ?? selectedCustomCallout?.groupColor
      ),
    };
  }, [customCalloutCategoryOptionMap, selectedCustomCallout]);
  const selectedAppearance = useMemo(
    () => getCalloutVisualFields(selectedCallout || {}),
    [selectedCallout]
  );
  const selectedLineStyle = useMemo(
    () => String(selectedCallout?.line_style ?? futureCalloutStyle?.line_style ?? 'orthogonal').trim().toLowerCase(),
    [futureCalloutStyle, selectedCallout]
  );
  const hasMultiSelectedCallouts = selectedPreviewCalloutKeys.length > 1;
  const selectedLabelSummary = selectedPin
    ? `${pinDisplayName(selectedPin)} - Pin ${selectedPin.pin_number || selectedPin.pinNumber || 'N/A'} - ${pinGroupName(selectedPin)}`
    : `${selectedCustomCallout?.label || 'Custom Label'} - Custom callout`;
  const selectedRoutePointCount = useMemo(
    () => getCalloutRoutePoints(selectedCallout).length,
    [selectedCallout]
  );

  const updateCallouts = useCallback((updater) => {
    const nextCallouts = updater(normalizedCallouts);
    onCalloutsChange(normalizePinCallouts(nextCallouts));
  }, [normalizedCallouts, onCalloutsChange]);

  const updateCalloutByKey = useCallback((key, partial) => {
    if (!key) {
      return;
    }

    const normalizedPartial = hasOwn(partial, 'bubble_scale') && !hasOwn(partial, 'label_scale')
      ? {
          ...partial,
          label_scale: partial.bubble_scale,
        }
      : partial;

    updateCallouts((currentCallouts) => currentCallouts.map((callout) => {
      if (getCalloutLayoutKey(callout) !== key) {
        return callout;
      }

      return {
        ...callout,
        ...normalizedPartial,
      };
    }));
  }, [updateCallouts]);

  const updateCalloutLabelPositions = useCallback((positionsByKey) => {
    const normalizedPositions = Object.entries(positionsByKey || {}).reduce((nextPositions, [key, position]) => {
      const labelX = Number(position?.label_x);
      const labelY = Number(position?.label_y);
      if (!key || !Number.isFinite(labelX) || !Number.isFinite(labelY)) {
        return nextPositions;
      }

      nextPositions[key] = {
        label_x: Number(labelX.toFixed(2)),
        label_y: Number(labelY.toFixed(2)),
      };
      return nextPositions;
    }, {});

    if (Object.keys(normalizedPositions).length === 0) {
      return;
    }

    updateCallouts((currentCallouts) => currentCallouts.map((callout) => {
      const key = getCalloutLayoutKey(callout);
      const nextPosition = normalizedPositions[key];
      if (!nextPosition) {
        return callout;
      }

      return {
        ...callout,
        ...nextPosition,
      };
    }));
  }, [updateCallouts]);

  const updateCalloutRoutePoints = useCallback((key, nextRoutePoints) => {
    if (!key) {
      return;
    }

    updateCalloutByKey(key, {
      route_points: normalizeCalloutRoutePoints(nextRoutePoints),
    });
  }, [updateCalloutByKey]);

  const deleteCalloutRoutePoint = useCallback((key, routePointIndex) => {
    const normalizedIndex = Number(routePointIndex);

    if (!key || !Number.isInteger(normalizedIndex) || normalizedIndex < 0) {
      return;
    }

    updateCallouts((currentCallouts) => currentCallouts.map((callout) => {
      if (getCalloutLayoutKey(callout) !== key) {
        return callout;
      }

      return {
        ...callout,
        route_points: getCalloutRoutePoints(callout)
          .filter((_, index) => index !== normalizedIndex),
      };
    }));
  }, [updateCallouts]);

  const updateVisualFieldsForAllCallouts = useCallback((partial) => {
    const visualFieldKeys = new Set([
      'text_scale',
      'bubble_scale',
      'label_scale',
      'bubble_width',
      'bubble_height',
      'bubble_radius',
      'bubble_stroke_width',
      'line_stroke_width',
      'anchor_radius',
      'rotation_deg',
      'line_attach_side',
      'line_style',
    ]);

    const normalizedPartial = hasOwn(partial, 'bubble_scale') && !hasOwn(partial, 'label_scale')
      ? {
          ...partial,
          label_scale: partial.bubble_scale,
        }
      : partial;

    const visualPartial = Object.entries(normalizedPartial).reduce((nextPartial, [key, value]) => {
      if (visualFieldKeys.has(key)) {
        nextPartial[key] = value;
      }

      return nextPartial;
    }, {});

    if (Object.keys(visualPartial).length === 0) {
      return;
    }

    updateCallouts((currentCallouts) => currentCallouts.map((callout) => applyVisualPartialToCallout(callout, visualPartial)));
  }, [updateCallouts]);


  const upsertCalloutForPin = useCallback((pin, partial) => {
    if (!pin) {
      return;
    }

    const key = getPinLayoutKey(pin);
    updateCallouts((currentCallouts) => {
      const pinAliases = new Set(getPinLayoutAliases(pin));
      const existing = currentCallouts.find((callout) => pinAliases.has(getCalloutLayoutKey(callout)));
      const existingKey = existing ? getCalloutLayoutKey(existing) : key;
      const normalizedPartial = hasOwn(partial, 'bubble_scale') && !hasOwn(partial, 'label_scale')
        ? {
            ...partial,
            label_scale: partial.bubble_scale,
          }
        : partial;
      const baseLabel = String(normalizedPartial?.label ?? existing?.label ?? pinDisplayName(pin));
      const baseAppearance = getCalloutVisualFields({
        ...existing,
        ...normalizedPartial,
      });
      const baseMetrics = getCalloutBoxMetrics(baseLabel, baseAppearance);
      const pinX = Number(pin.position_x);
      const pinY = Number(pin.position_y);
      const fallbackPoint = {
        x: Number.isFinite(pinX) ? pinX : rotatedBoardBox.minX + (rotatedBoardBox.width / 2),
        y: Number.isFinite(pinY) ? pinY : rotatedBoardBox.minY + (rotatedBoardBox.height / 2),
      };
      const defaultAnchorPoint = getDefaultPinAnchorPoint(pin, fallbackPoint, rotatedBoardBox);
      const baseCallout = existing || {
        callout_type: 'pin',
        pin_id: String(pin.id ?? pin.pin_id ?? pin.pinId ?? '').trim(),
        pin_number: String(pin.pin_number ?? pin.pinNumber ?? '').trim(),
        svg_id: String(pin.svg_id ?? pin.svgId ?? '').trim(),
        pin_name: String(pin.pin_name ?? pin.pinName ?? '').trim(),
        label: baseLabel,
        ...baseAppearance,
        label_x: Number((fallbackPoint.x - (baseMetrics.width / 2)).toFixed(2)),
        label_y: Number((fallbackPoint.y - (baseMetrics.height / 2)).toFixed(2)),
        anchor_x: toCoordinate(defaultAnchorPoint.x, fallbackPoint.x),
        anchor_y: toCoordinate(defaultAnchorPoint.y, fallbackPoint.y),
      };

      const nextCallout = {
        ...baseCallout,
        ...normalizedPartial,
      };

      const nextList = currentCallouts.filter((callout) => getCalloutLayoutKey(callout) !== existingKey);
      nextList.push(nextCallout);
      return nextList;
    });
  }, [rotatedBoardBox, updateCallouts]);

  const updateSelectedCallout = useCallback((partial) => {
    if (selectedCalloutKey) {
      updateCalloutByKey(selectedCalloutKey, partial);
      return;
    }

    if (selectedPin) {
      upsertCalloutForPin(selectedPin, partial);
    }
  }, [selectedCalloutKey, selectedPin, updateCalloutByKey, upsertCalloutForPin]);

  const updateSelectedCustomMetadataField = useCallback((field, value) => {
    if (!selectedCustomCallout) {
      return;
    }

    updateSelectedCallout({
      [field]: value,
    });
  }, [selectedCustomCallout, updateSelectedCallout]);

  const clearSelectedRoutePoints = useCallback(() => {
    if (!selectedCalloutKey) {
      return;
    }

    updateCalloutRoutePoints(selectedCalloutKey, []);
  }, [selectedCalloutKey, updateCalloutRoutePoints]);

  const selectSingleKey = useCallback((nextKey) => {
    setSelectedPinKey(nextKey);
    setAdditionalSelectedCalloutKeys((currentKeys) => (currentKeys.length === 0 ? currentKeys : []));
  }, [setSelectedPinKey]);
  const collapseMultiCalloutSelection = useCallback(() => {
    setAdditionalSelectedCalloutKeys((currentKeys) => (currentKeys.length === 0 ? currentKeys : []));
  }, []);

  const resolveCalloutSelection = useCallback((key, additive = false) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey || !calloutKeySet.has(normalizedKey)) {
      return null;
    }

    const currentExtraKeys = Array.from(new Set(
      additionalSelectedCalloutKeys.filter(
        (currentKey) => currentKey !== selectedPreviewCalloutKey && calloutKeySet.has(currentKey)
      )
    ));
    const currentSelectedKeys = selectedPreviewCalloutKey
      ? [selectedPreviewCalloutKey, ...currentExtraKeys]
      : [];

    if (!additive) {
      if (currentSelectedKeys.includes(normalizedKey)) {
        if (normalizedKey === selectedPreviewCalloutKey) {
          return {
            primaryKey: normalizedKey,
            extraKeys: currentExtraKeys,
            selectedKeys: currentSelectedKeys,
          };
        }

        const nextExtraKeys = [selectedPreviewCalloutKey, ...currentExtraKeys.filter((candidate) => candidate !== normalizedKey)];
        return {
          primaryKey: normalizedKey,
          extraKeys: nextExtraKeys,
          selectedKeys: [normalizedKey, ...nextExtraKeys],
        };
      }

      return {
        primaryKey: normalizedKey,
        extraKeys: [],
        selectedKeys: [normalizedKey],
      };
    }

    if (currentSelectedKeys.includes(normalizedKey)) {
      if (currentSelectedKeys.length <= 1) {
        return {
          primaryKey: normalizedKey,
          extraKeys: currentExtraKeys,
          selectedKeys: currentSelectedKeys,
        };
      }

      if (normalizedKey === selectedPreviewCalloutKey) {
        const remainingKeys = currentSelectedKeys.filter((candidate) => candidate !== normalizedKey);
        const [nextPrimaryKey, ...nextExtraKeys] = remainingKeys;
        return {
          primaryKey: nextPrimaryKey || '',
          extraKeys: nextExtraKeys,
          selectedKeys: remainingKeys,
        };
      }

      const nextExtraKeys = currentExtraKeys.filter((candidate) => candidate !== normalizedKey);
      return {
        primaryKey: selectedPreviewCalloutKey,
        extraKeys: nextExtraKeys,
        selectedKeys: [selectedPreviewCalloutKey, ...nextExtraKeys],
      };
    }

    if (!selectedPreviewCalloutKey) {
      return {
        primaryKey: normalizedKey,
        extraKeys: [],
        selectedKeys: [normalizedKey],
      };
    }

    return {
      primaryKey: normalizedKey,
      extraKeys: [selectedPreviewCalloutKey, ...currentExtraKeys],
      selectedKeys: [normalizedKey, selectedPreviewCalloutKey, ...currentExtraKeys],
    };
  }, [additionalSelectedCalloutKeys, calloutKeySet, selectedPreviewCalloutKey]);

  const applyCalloutSelection = useCallback((nextSelection) => {
    if (!nextSelection) {
      return;
    }

    setSelectedPinKey(nextSelection.primaryKey || '');
    setAdditionalSelectedCalloutKeys((currentKeys) => (
      areStringArraysEqual(currentKeys, nextSelection.extraKeys)
        ? currentKeys
        : nextSelection.extraKeys
    ));
  }, [setSelectedPinKey]);

  const updateFutureCalloutStyle = useCallback((partial) => {
    setFutureCalloutStyle((currentStyle) => {
      const normalizedPartial = hasOwn(partial, 'bubble_scale') && !hasOwn(partial, 'label_scale')
        ? {
            ...partial,
            label_scale: partial.bubble_scale,
          }
        : partial;

      return {
        ...currentStyle,
        ...normalizedPartial,
      };
    });
  }, []);

  const createCustomCallout = useCallback(() => {
    const nextIndex = customCallouts.length + 1;
    const label = `Custom Label ${nextIndex}`;
    const calloutStyle = getCalloutStyleTemplate(futureCalloutStyle);
    const labelMetrics = getCalloutBoxMetrics(label, calloutStyle);
    const baseX = rotatedBoardBox.minX + rotatedBoardBox.width + 48;
    const baseY = rotatedBoardBox.minY + 28 + ((nextIndex - 1) % 8) * CUSTOM_CALLOUT_STACK_OFFSET;
    const customId = `custom-${Date.now()}-${nextIndex}`;
    const nextCallout = {
      callout_type: CUSTOM_CALLOUT_TYPE,
      custom_id: customId,
      group_name: DEFAULT_CUSTOM_CALLOUT_GROUP_NAME,
      group_color: DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR,
      label,
      ...calloutStyle,
      label_x: Number(baseX.toFixed(2)),
      label_y: Number(baseY.toFixed(2)),
      anchor_x: Number((baseX + (labelMetrics.width / 2)).toFixed(2)),
      anchor_y: Number((baseY + labelMetrics.height + 42).toFixed(2)),
    };

    updateCallouts((currentCallouts) => [...currentCallouts, nextCallout]);
    selectSingleKey(`custom:${customId}`);
  }, [customCallouts.length, futureCalloutStyle, rotatedBoardBox, selectSingleKey, updateCallouts]);

  const removeCustomCallout = useCallback((calloutKey) => {
    if (!calloutKey) {
      return;
    }

    updateCallouts((currentCallouts) => currentCallouts.filter((callout) => getCalloutLayoutKey(callout) !== calloutKey));
  }, [updateCallouts]);

  const removeCalloutForPin = useCallback((pin) => {
    if (!pin) {
      return;
    }

  const pinAliases = new Set(getPinLayoutAliases(pin));
    updateCallouts((currentCallouts) => currentCallouts.filter((callout) => !pinAliases.has(getCalloutLayoutKey(callout))));
  }, [updateCallouts]);

  const getRenderedPinHolePoint = useCallback((pin) => {
    if (!pin || !previewRef.current) {
      return null;
    }

    const pinElements = Array.from(
      previewRef.current.querySelectorAll('.pin-hole, .pin-rect')
    );

    const pinElement = pinElements.find((element) => {
      if (!(element instanceof Element)) {
        return false;
      }

      // Ignore callout-generated bubbles.
      if (element.getAttribute('data-micropinouts-callout') === '1') {
        return false;
      }

      return doesElementMatchPin(element, pin);
    });

    const renderedCenter = getSvgElementCenter(pinElement, previewRef.current);

    if (renderedCenter) {
      return {
        x: Number(renderedCenter.x.toFixed(2)),
        y: Number(renderedCenter.y.toFixed(2)),
      };
    }

    const pinX = Number(pin.position_x ?? pin.position?.x);
    const pinY = Number(pin.position_y ?? pin.position?.y);

    if (Number.isFinite(pinX) && Number.isFinite(pinY)) {
      return {
        x: Number(pinX.toFixed(2)),
        y: Number(pinY.toFixed(2)),
      };
    }

    return null;
  }, []);

  const placePinAtPoint = useCallback((pin, point) => {
    if (!pin || !point) {
      return;
    }

    const placementPoint = snapToGrid ? snapPointToGrid(point, gridSize) : point;

    const existingCallout = getCalloutForPin(pin);
    const label = existingCallout?.label || pinDisplayName(pin);
    const appearance = getCalloutStyleTemplate(
      existingCallout || futureCalloutStyle || {
        text_scale: DEFAULT_CALLOUT_TEXT_SCALE,
        bubble_scale: DEFAULT_CALLOUT_BUBBLE_SCALE,
      }
    );

    const labelMetrics = getCalloutBoxMetrics(label, appearance);

    const pinHolePoint = getRenderedPinHolePoint(pin) || placementPoint;

    upsertCalloutForPin(pin, {
      label,
      ...appearance,

      // Label box appears where the user drops the tray item.
      label_x: Number((placementPoint.x - (labelMetrics.width / 2)).toFixed(2)),
      label_y: Number((placementPoint.y - (labelMetrics.height / 2)).toFixed(2)),

      // End bubble attaches to the real rendered SVG pin hole.
      anchor_x: pinHolePoint.x,
      anchor_y: pinHolePoint.y,
    });
  }, [
    futureCalloutStyle,
    getCalloutForPin,
    getRenderedPinHolePoint,
    gridSize,
    snapToGrid,
    upsertCalloutForPin,
  ]);

  const addDetectedPinsToCanvas = useCallback((pinsToPlace) => {
    const seenPinKeys = new Set();
    const pinsInPlacementOrder = (Array.isArray(pinsToPlace) ? pinsToPlace : []).filter((pin) => {
      const key = getPinLayoutKey(pin);
      if (!key || seenPinKeys.has(key) || getCalloutForPin(pin)) {
        return false;
      }

      seenPinKeys.add(key);
      return true;
    });

    if (disabled || pinsInPlacementOrder.length === 0 || !previewViewBox) {
      return;
    }

    const baseStyle = getCalloutStyleTemplate(
      futureCalloutStyle || {
        text_scale: DEFAULT_CALLOUT_TEXT_SCALE,
        bubble_scale: DEFAULT_CALLOUT_BUBBLE_SCALE,
        bubble_stroke_width: DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH,
      }
    );
    let nextExpandedViewBox = null;
    let firstPlacedPinKey = '';
    const placedPinKeys = [];

    updateCallouts((currentCallouts) => {
      const occupiedRects = currentCallouts
        .map((callout) => getCalloutRect(callout))
        .filter(Boolean);
      const occupiedAnchorRects = currentCallouts
        .map((callout) => {
          const anchorX = Number(callout.anchor_x);
          const anchorY = Number(callout.anchor_y);
          if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) {
            return null;
          }

          const calloutMetrics = getCalloutBoxMetrics(getCalloutLabelText(callout), callout);
          return getAnchorCollisionRect(
            { x: anchorX, y: anchorY },
            Math.max(calloutMetrics.anchorHitRadius, calloutMetrics.anchorRadius + 6)
          );
        })
        .filter(Boolean);
      const placedCallouts = [];
      const startX = previewViewBox.minX + AUTO_PLACE_CANVAS_PADDING;
      const startY = previewViewBox.minY + AUTO_PLACE_CANVAS_PADDING;
      const maxBottom = previewViewBox.minY + previewViewBox.height - AUTO_PLACE_CANVAS_PADDING;
      const existingLayoutRight = [...occupiedRects, ...occupiedAnchorRects].reduce((maxRight, rect) => {
        const rectRight = Number(rect?.right);
        return Number.isFinite(rectRight) ? Math.max(maxRight, rectRight) : maxRight;
      }, startX - AUTO_PLACE_COLUMN_GAP);
      let cursorX = occupiedRects.length > 0 || occupiedAnchorRects.length > 0
        ? existingLayoutRight + AUTO_PLACE_COLUMN_GAP
        : startX;
      let cursorY = startY;
      let columnWidth = 0;
      let layoutMinX = previewViewBox.minX;
      let layoutMinY = previewViewBox.minY;
      let layoutMaxX = previewViewBox.minX + previewViewBox.width;
      let layoutMaxY = previewViewBox.minY + previewViewBox.height;

      pinsInPlacementOrder.forEach((pin) => {
        const label = pinDisplayName(pin);
        const calloutStyle = {
          ...baseStyle,
        };
        const metrics = getCalloutBoxMetrics(label, calloutStyle);
        if (cursorY + metrics.height > maxBottom && cursorY > startY) {
          cursorX += columnWidth + AUTO_PLACE_COLUMN_GAP;
          cursorY = startY;
          columnWidth = 0;
        }

        const placedPoint = snapToGrid
          ? snapPointToGrid({ x: cursorX, y: cursorY }, gridSize)
          : {
              x: Number(cursorX.toFixed(2)),
              y: Number(cursorY.toFixed(2)),
            };
        const placedRect = {
          left: placedPoint.x,
          top: placedPoint.y,
          right: placedPoint.x + metrics.width,
          bottom: placedPoint.y + metrics.height,
          width: metrics.width,
          height: metrics.height,
        };

        const labelCenterPoint = {
          x: placedPoint.x + (metrics.width / 2),
          y: placedPoint.y + (metrics.height / 2),
        };

        const renderedPinHolePoint = getRenderedPinHolePoint(pin);
        const defaultAnchorPoint = renderedPinHolePoint || getDefaultPinAnchorPoint(
          pin,
          labelCenterPoint,
          rotatedBoardBox
        );

        const anchorBounds = getAnchorCollisionRect(
          defaultAnchorPoint,
          Math.max(metrics.anchorHitRadius, metrics.anchorRadius + 6)
        );
        const nextCallout = {
          callout_type: 'pin',
          pin_id: String(pin.id ?? pin.pin_id ?? pin.pinId ?? '').trim(),
          pin_number: String(pin.pin_number ?? pin.pinNumber ?? '').trim(),
          svg_id: String(pin.svg_id ?? pin.svgId ?? '').trim(),
          pin_name: String(pin.pin_name ?? pin.pinName ?? '').trim(),
          label,
          ...calloutStyle,
          label_x: Number(placedPoint.x.toFixed(2)),
          label_y: Number(placedPoint.y.toFixed(2)),
          anchor_x: toCoordinate(defaultAnchorPoint.x, labelCenterPoint.x),
          anchor_y: toCoordinate(defaultAnchorPoint.y, labelCenterPoint.y),
        };

        placedCallouts.push(nextCallout);
        occupiedRects.push(placedRect);
        occupiedAnchorRects.push(anchorBounds);
        cursorY = placedRect.bottom + AUTO_PLACE_ROW_GAP;
        columnWidth = Math.max(columnWidth, placedRect.width);
        layoutMinX = Math.min(layoutMinX, placedRect.left - AUTO_PLACE_CANVAS_PADDING, anchorBounds.left - AUTO_PLACE_CANVAS_PADDING);
        layoutMinY = Math.min(layoutMinY, placedRect.top - AUTO_PLACE_CANVAS_PADDING, anchorBounds.top - AUTO_PLACE_CANVAS_PADDING);
        layoutMaxX = Math.max(layoutMaxX, placedRect.right + AUTO_PLACE_CANVAS_PADDING, anchorBounds.right + AUTO_PLACE_CANVAS_PADDING);
        layoutMaxY = Math.max(layoutMaxY, placedRect.bottom + AUTO_PLACE_CANVAS_PADDING, anchorBounds.bottom + AUTO_PLACE_CANVAS_PADDING);

        if (!firstPlacedPinKey) {
          firstPlacedPinKey = getPinLayoutKey(pin);
        }

        placedPinKeys.push(getPinLayoutKey(pin));
      });

      if (placedCallouts.length === 0) {
        return currentCallouts;
      }

      const nextMinX = Math.min(previewViewBox.minX, layoutMinX);
      const nextMinY = Math.min(previewViewBox.minY, layoutMinY);
      const nextMaxX = Math.max(previewViewBox.minX + previewViewBox.width, layoutMaxX);
      const nextMaxY = Math.max(previewViewBox.minY + previewViewBox.height, layoutMaxY);

      nextExpandedViewBox = normalizeSvgViewBox({
        minX: nextMinX,
        minY: nextMinY,
        width: nextMaxX - nextMinX,
        height: nextMaxY - nextMinY,
      });

      return [...currentCallouts, ...placedCallouts];
    });

    if (firstPlacedPinKey) {
      selectSingleKey(firstPlacedPinKey);
    }

    if (placedPinKeys.length > 0) {
      const placedKeySet = new Set(placedPinKeys);
      setSelectedTrayPinKeys((currentKeys) => currentKeys.filter((key) => !placedKeySet.has(key)));
    }

    if (nextExpandedViewBox && !areSvgViewBoxesEqual(previewViewBox, nextExpandedViewBox)) {
      commitEditorViewBox(nextExpandedViewBox);
    }
  }, [
    commitEditorViewBox,
    disabled,
    futureCalloutStyle,
    getCalloutForPin,
    getRenderedPinHolePoint,
    gridSize,
    previewViewBox,
    rotatedBoardBox,
    selectSingleKey,
    snapToGrid,
    updateCallouts,
  ]);

  const addAllDiscoveredCalloutsToCanvas = useCallback(() => {
    addDetectedPinsToCanvas(unplacedPins);
  }, [addDetectedPinsToCanvas, unplacedPins]);

  const addSelectedDetectedCalloutsToCanvas = useCallback(() => {
    addDetectedPinsToCanvas(selectedTrayPins);
  }, [addDetectedPinsToCanvas, selectedTrayPins]);

  const updateCalloutTextScale = useCallback((pin, nextScaleValue) => {
    if (!pin) {
      return;
    }

    const existingCallout = getCalloutForPin(pin);
    const nextScale = normalizeCalloutTextScale(
      nextScaleValue,
      existingCallout?.text_scale ?? DEFAULT_CALLOUT_TEXT_SCALE
    );

    if (!existingCallout) {
      upsertCalloutForPin(pin, { text_scale: nextScale });
      return;
    }

    const label = existingCallout.label || pinDisplayName(pin);
    const currentMetrics = getCalloutBoxMetrics(label, existingCallout);
    const nextMetrics = getCalloutBoxMetrics(label, {
      ...existingCallout,
      text_scale: nextScale,
    });

    updateSelectedCallout({
      text_scale: nextScale,
      label_x: Number((existingCallout.label_x + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
      label_y: Number((existingCallout.label_y + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
    });
  }, [getCalloutForPin, updateSelectedCallout, upsertCalloutForPin]);

  const updateCustomCalloutTextScale = useCallback((callout, nextScaleValue) => {
    if (!callout) {
      return;
    }

    const key = getCalloutLayoutKey(callout);
    const nextScale = normalizeCalloutTextScale(nextScaleValue, callout.text_scale ?? DEFAULT_CALLOUT_TEXT_SCALE);
    const label = callout.label || 'Custom Label';
    const currentMetrics = getCalloutBoxMetrics(label, callout);
    const nextMetrics = getCalloutBoxMetrics(label, {
      ...callout,
      text_scale: nextScale,
    });

    updateCalloutByKey(key, {
      text_scale: nextScale,
      label_scale: callout.bubble_scale ?? callout.label_scale ?? DEFAULT_CALLOUT_BUBBLE_SCALE,
      label_x: Number((callout.label_x + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
      label_y: Number((callout.label_y + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
    });
  }, [updateCalloutByKey]);

  const updateCalloutBubbleDimension = useCallback((pin, dimensionKey, nextScaleValue) => {
    if (!pin) {
      return;
    }

    const normalizedDimensionKey = dimensionKey === 'bubble_height' ? 'bubble_height' : 'bubble_width';
    const existingCallout = getCalloutForPin(pin);
    const nextScale = normalizeCalloutBubbleDimension(
      normalizedDimensionKey,
      nextScaleValue,
      existingCallout?.[normalizedDimensionKey]
        ?? existingCallout?.bubble_scale
        ?? existingCallout?.label_scale
        ?? (
          normalizedDimensionKey === 'bubble_height'
            ? DEFAULT_CALLOUT_BUBBLE_HEIGHT
            : DEFAULT_CALLOUT_BUBBLE_WIDTH
        )
    );

    if (!existingCallout) {
      upsertCalloutForPin(pin, {
        [normalizedDimensionKey]: nextScale,
      });
      return;
    }

    const label = existingCallout.label || pinDisplayName(pin);
    const currentMetrics = getCalloutBoxMetrics(label, existingCallout);
    const nextMetrics = getCalloutBoxMetrics(label, {
      ...existingCallout,
      [normalizedDimensionKey]: nextScale,
    });

    updateSelectedCallout({
      [normalizedDimensionKey]: nextScale,
      label_x: Number((existingCallout.label_x + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
      label_y: Number((existingCallout.label_y + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
    });
  }, [getCalloutForPin, updateSelectedCallout, upsertCalloutForPin]);

  const updateCustomCalloutBubbleDimension = useCallback((callout, dimensionKey, nextScaleValue) => {
    if (!callout) {
      return;
    }

    const normalizedDimensionKey = dimensionKey === 'bubble_height' ? 'bubble_height' : 'bubble_width';
    const key = getCalloutLayoutKey(callout);
    const nextScale = normalizeCalloutBubbleDimension(
      normalizedDimensionKey,
      nextScaleValue,
      callout?.[normalizedDimensionKey]
        ?? callout.bubble_scale
        ?? callout.label_scale
        ?? (
          normalizedDimensionKey === 'bubble_height'
            ? DEFAULT_CALLOUT_BUBBLE_HEIGHT
            : DEFAULT_CALLOUT_BUBBLE_WIDTH
        )
    );
    const label = callout.label || 'Custom Label';
    const currentMetrics = getCalloutBoxMetrics(label, callout);
    const nextMetrics = getCalloutBoxMetrics(label, {
      ...callout,
      [normalizedDimensionKey]: nextScale,
    });

    updateCalloutByKey(key, {
      [normalizedDimensionKey]: nextScale,
      label_x: Number((callout.label_x + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
      label_y: Number((callout.label_y + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
    });
  }, [updateCalloutByKey]);

  const getPointFromEvent = getSvgPoint(previewRef.current);

  const handlePreviewClick = (event) => {
    if (disabled) {
      return;
    }

    if (panModeEnabled) {
      return;
    }

    if (dragState?.didMove) {
      setDragState(null);
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }

    const interactive = target.closest(PREVIEW_INTERACTIVE_SELECTOR);
    if (interactive) {
      const isCalloutElement = interactive.getAttribute('data-micropinouts-callout') === '1';
      if (isCalloutElement) {
        return;
      }

      const key = isCalloutElement
        ? getInteractivePinKey(interactive)
        : (() => {
            const matchedPin = findPinForElement(rotatedPins, interactive);
            return matchedPin ? getPinLayoutKey(matchedPin) : getInteractivePinKey(interactive);
          })();

      if (key) {
        selectSingleKey(key);
      }

      return;
    }

    if (hasMultiSelectedCallouts) {
      collapseMultiCalloutSelection();
      return;
    }

    if (!selectedPin) {
      return;
    }

    const point = getPointFromEvent(event);
    if (!point) {
      return;
    }

    placePinAtPoint(selectedPin, point);
  };

  const handlePreviewDoubleClick = useCallback((event) => {
    if (disabled) {
      return;
    }

    const routePointTarget = event.target instanceof Element
      ? event.target.closest('.pin-callout-route-point[data-micropinouts-route-point="1"], .pin-callout-route-point-hit[data-micropinouts-route-point="1"]')
      : null;

    if (!routePointTarget) {
      return;
    }

    const key = getInteractivePinKey(routePointTarget);
    const routePointIndex = Number(routePointTarget.getAttribute('data-route-point-index'));

    if (!key || !Number.isInteger(routePointIndex)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    deleteCalloutRoutePoint(key, routePointIndex);
  }, [deleteCalloutRoutePoint, disabled]);

  useEffect(() => {
    if (!hasMultiSelectedCallouts) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      collapseMultiCalloutSelection();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [collapseMultiCalloutSelection, hasMultiSelectedCallouts]);

  const handleTrayPinClick = useCallback((pin, hasCallout) => {
    const key = getPinLayoutKey(pin);
    selectSingleKey(key);

    if (hasCallout) {
      return;
    }

    setSelectedTrayPinKeys((currentKeys) => (
      currentKeys.includes(key)
        ? currentKeys.filter((currentKey) => currentKey !== key)
        : [...currentKeys, key]
    ));
  }, [selectSingleKey]);

  const handleTrayDragStart = (event, pin) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    const key = getPinLayoutKey(pin);
    selectSingleKey(key);
    setSelectedTrayPinKeys((currentKeys) => (
      currentKeys.includes(key) ? currentKeys : [...currentKeys, key]
    ));
    setDraggedTrayPinKey(key);
    event.dataTransfer.setData('text/pin-layout-key', key);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleTrayDragEnd = () => {
    setDraggedTrayPinKey('');
    setIsDropActive(false);
  };

  const handlePreviewDragOver = (event) => {
    if (disabled) {
      return;
    }

    const pinKey = draggedTrayPinKey || event.dataTransfer.getData('text/pin-layout-key');
    if (!pinKey) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!isDropActive) {
      setIsDropActive(true);
    }
  };

  const handlePreviewDragLeave = (event) => {
    if (!previewRef.current?.contains(event.relatedTarget)) {
      setIsDropActive(false);
    }
  };

  const handlePreviewDrop = (event) => {
    if (disabled) {
      return;
    }

    const pinKey = draggedTrayPinKey || event.dataTransfer.getData('text/pin-layout-key');
    if (!pinKey) {
      return;
    }

    event.preventDefault();
    setIsDropActive(false);
    setDraggedTrayPinKey('');
    const pin = rotatedPins.find((candidate) => getPinLayoutKey(candidate) === pinKey);
    const point = getPointFromEvent(event);
    if (!pin || !point) {
      return;
    }

    selectSingleKey(pinKey);
    placePinAtPoint(pin, point);
  };

  const handlePointerDown = (event) => {
    if (disabled) {
      return;
    }

    if (panModeEnabled) {
      const point = getPointFromEvent(event);
      if (!point || !previewRef.current) {
        return;
      }

      const rect = previewRef.current.getBoundingClientRect();
      setDragState({
        mode: 'pan',
        lastClientX: event.clientX,
        lastClientY: event.clientY,
        viewWidth: previewViewBox.width,
        viewHeight: previewViewBox.height,
        rectWidth: rect.width || 1,
        rectHeight: rect.height || 1,
        didMove: false,
      });
      return;
    }

    const calloutTarget = event.target instanceof Element
      ? event.target.closest('[data-callout-key][data-micropinouts-callout="1"]')
      : null;
    const routePointTarget = event.target instanceof Element
      ? event.target.closest('.pin-callout-route-point[data-micropinouts-route-point="1"], .pin-callout-route-point-hit[data-micropinouts-route-point="1"]')
      : null;
    const bubbleTarget = event.target instanceof Element
      ? event.target.closest('.pin-callout-anchor[data-micropinouts-anchor="1"], .pin-callout-anchor-hit[data-micropinouts-anchor="1"]')
      : null;
    const lineTarget = event.target instanceof Element
      ? event.target.closest('.pin-callout-line[data-micropinouts-callout="1"], .pin-callout-line-hit[data-micropinouts-callout="1"]')
      : null;
    const labelTarget = event.target instanceof Element
      ? event.target.closest('.pin-callout-bubble[data-micropinouts-callout="1"]')
      : null;
    let nextSelection = null;

    if (calloutTarget) {
      const key = getInteractivePinKey(calloutTarget);
      nextSelection = resolveCalloutSelection(key, event.shiftKey);
      if (!nextSelection) {
        return;
      }

      applyCalloutSelection(nextSelection);
    }

    if (routePointTarget) {
      const key = getInteractivePinKey(routePointTarget);
      const callout = calloutMap.get(key);
      const routePointIndex = Number(routePointTarget.getAttribute('data-route-point-index'));

      if (!callout || !Number.isInteger(routePointIndex) || !nextSelection?.selectedKeys?.includes(key)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setDragState({
        key,
        mode: 'route-point',
        routePointIndex,
        viewBoxLock: {
          minX: previewViewBox.minX,
          minY: previewViewBox.minY,
          width: previewViewBox.width,
          height: previewViewBox.height,
        },
        didMove: false,
      });
      return;
    }

    if (bubbleTarget) {
      const key = getInteractivePinKey(bubbleTarget);
      const callout = calloutMap.get(key);
      const point = getPointFromEvent(event);
      if (!callout || !point || !nextSelection?.selectedKeys?.includes(key)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setDragState({
        key,
        mode: 'anchor',
        viewBoxLock: {
          minX: previewViewBox.minX,
          minY: previewViewBox.minY,
          width: previewViewBox.width,
          height: previewViewBox.height,
        },
        didMove: false,
      });
      return;
    }

    if (lineTarget) {
      const key = getInteractivePinKey(lineTarget);
      const callout = calloutMap.get(key);
      const point = getPointFromEvent(event);
      const matchedPin = rotatedPins.find((candidate) => doesPinMatchLayoutKey(candidate, key));
      const fallbackAnchorPoint = matchedPin
        ? {
            x: Number(matchedPin.position_x),
            y: Number(matchedPin.position_y),
          }
        : null;

      if (!callout || !point) {
        return;
      }

      if (!selectedPreviewCalloutKeySet.has(key)) {
        return;
      }

      const insertedPoint = snapToGrid
        ? snapPointToGrid(point, gridSize)
        : point;
      const routePoints = getCalloutRoutePoints(callout);
      const insertIndex = getClosestCalloutRouteInsertIndex(callout, insertedPoint, fallbackAnchorPoint);
      const nextRoutePoints = [
        ...routePoints.slice(0, insertIndex),
        {
          x: Number(insertedPoint.x.toFixed(2)),
          y: Number(insertedPoint.y.toFixed(2)),
        },
        ...routePoints.slice(insertIndex),
      ];

      event.preventDefault();
      event.stopPropagation();
      updateCalloutRoutePoints(key, nextRoutePoints);
      setDragState({
        key,
        mode: 'route-point',
        routePointIndex: insertIndex,
        viewBoxLock: {
          minX: previewViewBox.minX,
          minY: previewViewBox.minY,
          width: previewViewBox.width,
          height: previewViewBox.height,
        },
        didMove: false,
      });
      return;
    }

    if (!labelTarget) {
      return;
    }

    const key = getInteractivePinKey(labelTarget);
    const callout = calloutMap.get(key);
    if (!callout) {
      return;
    }

    const point = getPointFromEvent(event);
    if (!point) {
      return;
    }

    if (!nextSelection?.selectedKeys?.includes(key)) {
      return;
    }

    const dragSelectionKeys = nextSelection.selectedKeys;
    const labelPositions = dragSelectionKeys.reduce((positionsByKey, currentKey) => {
      const currentCallout = calloutMap.get(currentKey);
      if (!currentCallout) {
        return positionsByKey;
      }

      positionsByKey[currentKey] = {
        label_x: Number(currentCallout.label_x),
        label_y: Number(currentCallout.label_y),
      };
      return positionsByKey;
    }, {});

    event.preventDefault();
    event.stopPropagation();
    setDragState({
      key,
      mode: 'label',
      selectedKeys: dragSelectionKeys,
      labelPositions,
      pointerStartX: point.x,
      pointerStartY: point.y,
      viewBoxLock: {
        minX: previewViewBox.minX,
        minY: previewViewBox.minY,
        width: previewViewBox.width,
        height: previewViewBox.height,
      },
      didMove: false,
    });
  };

  useEffect(() => {
    if (!dragState) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (dragState.mode === 'pan') {
        const deltaClientX = event.clientX - dragState.lastClientX;
        const deltaClientY = event.clientY - dragState.lastClientY;
        const deltaSvgX = (deltaClientX / dragState.rectWidth) * dragState.viewWidth;
        const deltaSvgY = (deltaClientY / dragState.rectHeight) * dragState.viewHeight;

        setEditorViewBoxLocally((currentViewBox) => translateViewBox(
          currentViewBox || previewViewBox,
          -deltaSvgX,
          -deltaSvgY
        ));
        setDragState((current) => current ? {
          ...current,
          lastClientX: event.clientX,
          lastClientY: event.clientY,
          didMove: true,
        } : current);
        return;
      }

      const point = getSvgPoint(previewRef.current)(event);
      if (!point) {
        return;
      }

      const callout = calloutMap.get(dragState.key);
      if (!callout) {
        return;
      }

      if (dragState.mode === 'anchor') {
        const hoveredPinTarget = findBoardPinSnapTarget(event, previewRef.current, rotatedPins);
        const rawAnchorPoint = hoveredPinTarget?.point || point;
        const nextAnchorPoint = snapToGrid && !hoveredPinTarget
          ? snapPointToGrid(rawAnchorPoint, gridSize)
          : rawAnchorPoint;

        updateCalloutByKey(dragState.key, {
          anchor_x: toCoordinate(nextAnchorPoint.x, point.x),
          anchor_y: toCoordinate(nextAnchorPoint.y, point.y),
        });
      } else if (dragState.mode === 'route-point') {
        if (!Number.isInteger(dragState.routePointIndex) || dragState.routePointIndex < 0) {
          return;
        }

        const nextPoint = snapToGrid
          ? snapPointToGrid(point, gridSize)
          : point;
        const nextRoutePoints = [...getCalloutRoutePoints(callout)];
        nextRoutePoints[dragState.routePointIndex] = {
          x: Number(nextPoint.x.toFixed(2)),
          y: Number(nextPoint.y.toFixed(2)),
        };
        updateCalloutRoutePoints(dragState.key, nextRoutePoints);
      } else {
        const activeStartPosition = dragState.labelPositions?.[dragState.key];
        if (!activeStartPosition) {
          return;
        }

        const deltaX = point.x - dragState.pointerStartX;
        const deltaY = point.y - dragState.pointerStartY;
        const rawActiveLabelPoint = {
          x: activeStartPosition.label_x + deltaX,
          y: activeStartPosition.label_y + deltaY,
        };
        const nextActiveLabelPoint = snapToGrid
          ? snapPointToGrid(rawActiveLabelPoint, gridSize)
          : rawActiveLabelPoint;
        const appliedDeltaX = nextActiveLabelPoint.x - activeStartPosition.label_x;
        const appliedDeltaY = nextActiveLabelPoint.y - activeStartPosition.label_y;
        const nextPositions = (Array.isArray(dragState.selectedKeys) ? dragState.selectedKeys : [dragState.key]).reduce((positionsByKey, currentKey) => {
          const startPosition = dragState.labelPositions?.[currentKey];
          if (!startPosition) {
            return positionsByKey;
          }

          positionsByKey[currentKey] = {
            label_x: startPosition.label_x + appliedDeltaX,
            label_y: startPosition.label_y + appliedDeltaY,
          };
          return positionsByKey;
        }, {});

        updateCalloutLabelPositions(nextPositions);
      }

      setDragState((current) => current ? { ...current, didMove: true } : current);
    };

    const handlePointerUp = () => {
      if (dragState.mode === 'pan' && dragState.didMove && typeof onViewBoxChange === 'function') {
        const currentViewBox = editorViewBoxRef.current || defaultEditorViewBox;
        if (!areSvgViewBoxesEqual(initialViewBox, currentViewBox)) {
          onViewBoxChange(currentViewBox);
        }
      }

      setTimeout(() => setDragState(null), 0);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [
    calloutMap,
    defaultEditorViewBox,
    dragState,
    initialViewBox,
    onViewBoxChange,
    previewViewBox,
    rotatedPins,
    setEditorViewBoxLocally,
    updateCalloutLabelPositions,
    updateCalloutByKey,
    updateCalloutRoutePoints,
    gridSize,
    snapToGrid,
  ]);

  const updateSelectedVisualSetting = useCallback((partial) => {
    updateSelectedCallout(partial);

    if (applyVisualChangesToAll) {
      updateFutureCalloutStyle(partial);
    }

    if (applyVisualChangesToAllExisting) {
      updateVisualFieldsForAllCallouts(partial);
    }
  }, [
    applyVisualChangesToAll,
    applyVisualChangesToAllExisting,
    updateSelectedCallout,
    updateFutureCalloutStyle,
    updateVisualFieldsForAllCallouts,
  ]);

  const updateTextScaleWithApplyMode = useCallback((nextScaleValue) => {
    const nextScale = normalizeCalloutTextScale(
      nextScaleValue,
      selectedAppearance.text_scale
    );

    if (applyVisualChangesToAll) {
      updateFutureCalloutStyle({
        text_scale: nextScale,
      });
    }

    if (applyVisualChangesToAllExisting) {
      updateVisualFieldsForAllCallouts({
        text_scale: nextScale,
      });
      return;
    }

    if (selectedPin) {
      updateCalloutTextScale(selectedPin, nextScale);
      return;
    }

    if (selectedCustomCallout) {
      updateCustomCalloutTextScale(selectedCustomCallout, nextScale);
    }
  }, [
    applyVisualChangesToAll,
    applyVisualChangesToAllExisting,
    selectedAppearance.text_scale,
    selectedPin,
    selectedCustomCallout,
    updateFutureCalloutStyle,
    updateVisualFieldsForAllCallouts,
    updateCalloutTextScale,
    updateCustomCalloutTextScale,
  ]);

  const updateBubbleDimensionWithApplyMode = useCallback((dimensionKey, nextScaleValue) => {
    const normalizedDimensionKey = dimensionKey === 'bubble_height' ? 'bubble_height' : 'bubble_width';
    const nextScale = normalizeCalloutBubbleDimension(
      normalizedDimensionKey,
      nextScaleValue,
      selectedAppearance[normalizedDimensionKey]
    );
    const visualPartial = {
      [normalizedDimensionKey]: nextScale,
    };

    if (applyVisualChangesToAll) {
      updateFutureCalloutStyle(visualPartial);
    }

    if (applyVisualChangesToAllExisting) {
      updateVisualFieldsForAllCallouts(visualPartial);
      return;
    }

    if (selectedPin) {
      updateCalloutBubbleDimension(selectedPin, normalizedDimensionKey, nextScale);
      return;
    }

    if (selectedCustomCallout) {
      updateCustomCalloutBubbleDimension(selectedCustomCallout, normalizedDimensionKey, nextScale);
    }
  }, [
    applyVisualChangesToAll,
    applyVisualChangesToAllExisting,
    selectedAppearance,
    selectedPin,
    selectedCustomCallout,
    updateFutureCalloutStyle,
    updateVisualFieldsForAllCallouts,
    updateCalloutBubbleDimension,
    updateCustomCalloutBubbleDimension,
  ]);

  return (
    <EditorShell>
      <PreviewPanel>
        <PreviewToolbar>
          <ToolbarTitle>
            <ToolbarHeading>Rotation And Label Placement</ToolbarHeading>
            <ToolbarHint>Drag detected labels onto the board, add custom callouts when needed, Shift-click placed callouts to select a group, drag selected labels to move them together, drag the end bubble to point the line at the right spot, and click a connector wire to add editable bends.</ToolbarHint>
          </ToolbarTitle>
          <ToolbarActions>
            <ActionButton
              type="button"
              onClick={() => onRotationChange(-90)}
              disabled={disabled}
              $iconOnly
              title="Rotate Left"
              aria-label="Rotate Left"
            >
              <RotateCcw />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => onRotationChange(90)}
              disabled={disabled}
              $iconOnly
              title="Rotate Right"
              aria-label="Rotate Right"
            >
              <RotateCw />
            </ActionButton>
            {typeof onNormalizeCanvasSize === 'function' && (
                <ActionButton
                type="button"
                onClick={() => {
                  onNormalizeCanvasSize();
                  commitEditorViewBox(defaultEditorViewBox);
                }}
                disabled={disabled}
                $iconOnly
                title="Normalize Size"
                aria-label="Normalize Size"
              >
                <Maximize2 />
              </ActionButton>
            )}

            {/* <ActionButton
              type="button"
              onClick={autoPlaceAllCallouts}
              disabled={disabled || rotatedPins.length === 0}
              $iconOnly={false}
              title="Auto Place Callouts"
            >
              Auto Place
            </ActionButton> */}

            <ActionButton
              type="button"
              onClick={() => setPanModeEnabled((currentMode) => !currentMode)}
              disabled={disabled}
              $iconOnly
              $active={panModeEnabled}
              title={panModeEnabled ? 'Stop Dragging Board' : 'Drag Board'}
              aria-label={panModeEnabled ? 'Stop Dragging Board' : 'Drag Board'}
              aria-pressed={panModeEnabled}
            >
              <Hand />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => {
                commitEditorViewBox((currentViewBox) => buildCenteredScaledViewBox(
                  currentViewBox || defaultEditorViewBox,
                  1 / REFERENCE_VIEW_SCALE_STEP
                ));
              }}
              disabled={disabled}
              $iconOnly
              title="Zoom Out"
              aria-label="Zoom Out"
            >
              <ZoomOut />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => {
                commitEditorViewBox(defaultEditorViewBox);
              }}
              disabled={disabled}
              $iconOnly
              title="Reset View"
              aria-label="Reset View"
            >
              <LocateFixed />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => {
                commitEditorViewBox((currentViewBox) => buildCenteredScaledViewBox(
                  currentViewBox || defaultEditorViewBox,
                  REFERENCE_VIEW_SCALE_STEP
                ));
              }}
              disabled={disabled}
              $iconOnly
              title="Zoom In"
              aria-label="Zoom In"
            >
              <ZoomIn />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => onCalloutsChange([])}
              disabled={disabled || normalizedCallouts.length === 0}
              $iconOnly
              title="Clear All Labels"
              aria-label="Clear All Labels"
            >
              <X />
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => setShowGrid((currentValue) => !currentValue)}
              disabled={disabled}
              $iconOnly
              $active={showGrid}
              title={showGrid ? 'Hide Grid' : 'Show Grid'}
              aria-label={showGrid ? 'Hide Grid' : 'Show Grid'}
              aria-pressed={showGrid}
            >
              <Grid3X3 />
            </ActionButton>

            <CheckboxLabel title="Snap labels and end bubbles to the grid">
              <input
                type="checkbox"
                checked={snapToGrid}
                onChange={(event) => setSnapToGrid(event.target.checked)}
                disabled={disabled}
              />
              Snap
            </CheckboxLabel>

            <ToolbarNumberInput
              type="number"
              min="2"
              max="100"
              step="1"
              value={gridSize}
              onChange={(event) => {
                const nextGridSize = Number(event.target.value);
                setGridSize(Number.isFinite(nextGridSize) && nextGridSize > 0 ? nextGridSize : 10);
              }}
              disabled={disabled}
              title="Grid size"
            />
          </ToolbarActions>
        </PreviewToolbar>
        <PreviewViewport>
          <PreviewCanvas
            ref={previewRef}
            $heightPx={previewCanvasHeight}
            $dropActive={isDropActive}
            $showGrid={showGrid}
            $gridSize={gridSize}
            onDragOver={handlePreviewDragOver}
            onDragLeave={handlePreviewDragLeave}
            onDrop={handlePreviewDrop}
          >
            <PreviewMarkup
              $panMode={panModeEnabled}
              onClick={handlePreviewClick}
              onDoubleClick={handlePreviewDoubleClick}
              onPointerDown={handlePointerDown}
              dangerouslySetInnerHTML={{ __html: previewSvg }}
            />
          </PreviewCanvas>
        </PreviewViewport>
      </PreviewPanel>

      <Sidebar>
        <SidebarTopRow>
        <PanelCard>
           <PanelHeaderRow>
            <PanelTitle style={{ marginBottom: 0 }}>Detected Pin Tray</PanelTitle>
            <MiniActions>
              {selectedTrayPins.length > 1 ? (
                <CompactButton
                  type="button"
                  onClick={addSelectedDetectedCalloutsToCanvas}
                  disabled={disabled || selectedTrayPins.length === 0}
                >
                  <Plus size={14} />
                  Add Selected to Canvas
                </CompactButton>
              ) : null}
              <CompactButton
                type="button"
                onClick={addAllDiscoveredCalloutsToCanvas}
                disabled={disabled || unplacedPins.length === 0}
              >
                <Plus size={14} />
                Add All to Canvas
              </CompactButton>
            </MiniActions>
          </PanelHeaderRow>
          <PanelText>Click tray chips to select one or more pins, then add just those to the canvas, or drag individual chips onto the board. Blank gaps mean that pin already has a placed label on the canvas.</PanelText>
          {rotatedPins.length === 0 ? (
            <EmptyState>No detected pin markers were found in this upload.</EmptyState>
          ) : (
            <TrayGrid>
              {rotatedPins.map((pin) => {
                const key = getPinLayoutKey(pin);
                const hasCallout = Boolean(getCalloutForPin(pin));
                return (
                  <LabelChip
                    key={key}
                    type="button"
                    $placed={hasCallout}
                    $selected={!hasCallout && selectedTrayPinKeySet.has(key)}
                    draggable={!hasCallout}
                    onDragStart={hasCallout ? undefined : (event) => handleTrayDragStart(event, pin)}
                    onDragEnd={hasCallout ? undefined : handleTrayDragEnd}
                    onClick={() => handleTrayPinClick(pin, hasCallout)}
                  >
                    <LabelChipTitle>{pinDisplayName(pin)}</LabelChipTitle>
                    <LabelChipMeta>
                      Pin {pin.pin_number || pin.pinNumber || 'N/A'} · {hasCallout ? 'placed' : 'drag onto board'}
                    </LabelChipMeta>
                  </LabelChip>
                );
              })}
            </TrayGrid>
          )}
        </PanelCard>

        <PanelCard>
          <PanelHeaderRow>
            <PanelTitle style={{ marginBottom: 0 }}>Custom Callouts</PanelTitle>
            <CompactButton
              type="button"
              onClick={createCustomCallout}
              disabled={disabled}
            >
              <Plus size={14} />
              Add
            </CompactButton>
          </PanelHeaderRow>
          {customCallouts.length === 0 ? (
            <EmptyState>Add a custom callout when you need a label that is not tied to a detected pin marker.</EmptyState>
          ) : (
            <PinList>
              {customCallouts.map((callout, index) => {
                const key = getCalloutLayoutKey(callout);
                return (
                  <PinButton
                    key={key}
                    type="button"
                    $selected={selectedPreviewCalloutKeySet.has(key)}
                    onClick={(event) => {
                      const nextSelection = resolveCalloutSelection(key, event.shiftKey);
                      applyCalloutSelection(nextSelection);
                    }}
                  >
                    <PinHeading>
                      <PinName>{customCalloutDisplayName(callout, index)}</PinName>
                      <PlacementBadge $placed>
                        Custom
                      </PlacementBadge>
                    </PinHeading>
                    <PinMeta>Manual callout · drag the label box and end bubble on the canvas</PinMeta>
                  </PinButton>
                );
              })}
            </PinList>
          )}
        </PanelCard>
        </SidebarTopRow>

        <PanelCard>
          <PanelTitle>Selected Label</PanelTitle>
          {!selectedPin && !selectedCustomCallout ? (
            <EmptyState>Select a tray pin, a placed label on the board, or a custom callout to edit its label.</EmptyState>
          ) : (
            <FormStack>
              <PanelText>
                {selectedLabelSummary}
                {/*
                {selectedPin ? (
                {pinDisplayName(selectedPin)} · Pin {selectedPin.pin_number || selectedPin.pinNumber || 'N/A'} · {pinGroupName(selectedPin)}
                ) : (
                  `${selectedCustomCallout?.label || 'Custom Label'} · Custom callout`
                )}
                */}
              </PanelText>
              {hasMultiSelectedCallouts ? (
                <PanelText>{selectedPreviewCalloutKeys.length} callouts are selected. Drag any selected label to move the whole group together. Press Esc or click empty canvas space to collapse back to the active label.</PanelText>
              ) : null}
              {!hasMultiSelectedCallouts && selectedRoutePointCount > 0 ? (
                <PanelText>
                  This connector has {selectedRoutePointCount} manual bend{selectedRoutePointCount === 1 ? '' : 's'}.
                  Drag a bend to reroute it, double-click a bend to remove it, or reset the route to go back to the automatic leader line.
                </PanelText>
              ) : null}
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={applyVisualChangesToAll}
                  onChange={(event) => setApplyVisualChangesToAll(event.target.checked)}
                  disabled={disabled}
                />
                Use these size/style changes for future callouts
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={applyVisualChangesToAllExisting}
                  onChange={(event) => setApplyVisualChangesToAllExisting(event.target.checked)}
                  disabled={disabled}
                />
                Apply size/style changes to all existing callouts
              </CheckboxLabel>
              <FieldLabel>
                Label Text
                <FieldInput
                  type="text"
                  value={selectedCallout?.label ?? (selectedPin ? pinDisplayName(selectedPin) : 'Custom Label')}
                  onChange={(event) => {
                    const nextLabel = event.target.value;
                    const currentLabel = selectedCallout?.label || (selectedPin ? pinDisplayName(selectedPin) : 'Custom Label');

                    if (selectedCallout) {
                      const currentMetrics = getCalloutBoxMetrics(currentLabel, {
                        ...(selectedCallout || {}),
                        ...selectedAppearance,
                      });
                      const nextMetrics = getCalloutBoxMetrics(nextLabel, {
                        ...(selectedCallout || {}),
                        ...selectedAppearance,
                      });

                      updateSelectedCallout({
                        label: nextLabel,
                        label_x: Number((selectedCallout.label_x + ((currentMetrics.width - nextMetrics.width) / 2)).toFixed(2)),
                        label_y: Number((selectedCallout.label_y + ((currentMetrics.height - nextMetrics.height) / 2)).toFixed(2)),
                      });
                      return;
                    }

                    if (selectedPin) {
                      upsertCalloutForPin(selectedPin, { label: nextLabel });
                    }
                  }}
                  disabled={disabled}
                />
              </FieldLabel>
              {selectedCustomCallout ? (
                <FieldLabel>
                  Category
                  <FieldInput
                    as="select"
                    value={selectedCustomCalloutCategory?.value || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME}
                    onChange={(event) => {
                      const nextGroupName = String(event.target.value || '').trim() || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME;
                      const nextOption = customCalloutCategoryOptionMap.get(nextGroupName.toLowerCase());

                      updateSelectedCallout({
                        group_name: nextOption?.label || nextGroupName,
                        group_color: nextOption?.color || resolveCalloutGroupColor(nextGroupName),
                      });
                    }}
                    disabled={disabled}
                  >
                    {customCalloutCategoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FieldInput>
                </FieldLabel>
              ) : null}
              {selectedCustomCallout ? (
                <>
                  <PanelText>
                    These details are saved with the custom callout and appear in the public board sidebar when someone clicks it.
                  </PanelText>
                  <InlineFields>
                    <FieldLabel>
                      Pin Role
                      <FieldInput
                        as="select"
                        value={selectedCustomCallout.pin_role || ''}
                        onChange={(event) => updateSelectedCustomMetadataField('pin_role', event.target.value)}
                        disabled={disabled}
                      >
                        {CUSTOM_CALLOUT_PIN_ROLE_OPTIONS.map((option) => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FieldInput>
                    </FieldLabel>
                    <FieldLabel>
                      Primary Function
                      <FieldInput
                        type="text"
                        value={selectedCustomCallout.functions || ''}
                        onChange={(event) => updateSelectedCustomMetadataField('functions', event.target.value)}
                        placeholder="e.g. Digital I/O, power rail, reset"
                        disabled={disabled}
                      />
                    </FieldLabel>
                  </InlineFields>
                  <InlineFields>
                    <FieldLabel>
                      Current Limit (mA)
                      <FieldInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={selectedCustomCallout.current_limit_ma ?? ''}
                        onChange={(event) => updateSelectedCustomMetadataField('current_limit_ma', event.target.value)}
                        placeholder="e.g. 20"
                        disabled={disabled}
                      />
                    </FieldLabel>
                    <FieldLabel>
                      Voltage Min (V)
                      <FieldInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={selectedCustomCallout.voltage_min_v ?? ''}
                        onChange={(event) => updateSelectedCustomMetadataField('voltage_min_v', event.target.value)}
                        placeholder="e.g. 0"
                        disabled={disabled}
                      />
                    </FieldLabel>
                    <FieldLabel>
                      Voltage Max (V)
                      <FieldInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={selectedCustomCallout.voltage_max_v ?? ''}
                        onChange={(event) => updateSelectedCustomMetadataField('voltage_max_v', event.target.value)}
                        placeholder="e.g. 5"
                        disabled={disabled}
                      />
                    </FieldLabel>
                  </InlineFields>
                  <InlineFields>
                    <FieldLabel>
                      5V Tolerant
                      <CheckboxLabel>
                        <input
                          type="checkbox"
                          checked={Boolean(selectedCustomCallout.is_5v_tolerant)}
                          onChange={(event) => updateSelectedCustomMetadataField('is_5v_tolerant', event.target.checked)}
                          disabled={disabled}
                        />
                        <span>Safe for 5V logic</span>
                      </CheckboxLabel>
                    </FieldLabel>
                    <FieldLabel>
                      Pin With Caution
                      <CheckboxLabel>
                        <input
                          type="checkbox"
                          checked={Boolean(selectedCustomCallout.use_with_caution)}
                          onChange={(event) => updateSelectedCustomMetadataField('use_with_caution', event.target.checked)}
                          disabled={disabled}
                        />
                        <span>Include in caution info</span>
                      </CheckboxLabel>
                    </FieldLabel>
                  </InlineFields>
                  <FieldLabel $fullWidth>
                    Capability Tags
                    <CapabilityTagsField
                      value={selectedCustomCallout.capability_tags}
                      onChange={(nextTags) => updateSelectedCustomMetadataField('capability_tags', nextTags)}
                      suggestedTags={CUSTOM_CALLOUT_SUGGESTED_CAPABILITY_TAGS}
                      disabled={disabled}
                      placeholder="e.g. gpio, i2c-sda, reset"
                      inputAriaLabel={`Capability tags for ${selectedCustomCallout.label || 'selected custom callout'}`}
                      helperText="Type free-form tags, or click a suggested tag to insert it."
                    />
                  </FieldLabel>
                  {Boolean(selectedCustomCallout.use_with_caution) ? (
                    <FieldLabel $fullWidth>
                      Caution Reason
                      <FieldTextArea
                        value={selectedCustomCallout.caution_reason || ''}
                        onChange={(event) => updateSelectedCustomMetadataField('caution_reason', event.target.value)}
                        placeholder="Explain why this custom callout should be used carefully..."
                        disabled={disabled}
                      />
                    </FieldLabel>
                  ) : null}
                  <FieldLabel $fullWidth>
                    Alternate Functions
                    <FieldTextArea
                      value={selectedCustomCallout.alternate_functions || ''}
                      onChange={(event) => updateSelectedCustomMetadataField('alternate_functions', event.target.value)}
                      placeholder="e.g. SPI MOSI, PWM output, wake pin"
                      disabled={disabled}
                    />
                  </FieldLabel>
                  <FieldLabel $fullWidth>
                    Description
                    <FieldTextArea
                      value={selectedCustomCallout.description || ''}
                      onChange={(event) => updateSelectedCustomMetadataField('description', event.target.value)}
                      placeholder="Add public notes, warnings, or usage details for this custom callout..."
                      disabled={disabled}
                    />
                  </FieldLabel>
                </>
              ) : null}
              <InlineFields>
                <FieldLabel>
                  Text Size
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_TEXT_SCALE}
                      max={MAX_CALLOUT_TEXT_SCALE}
                      step="0.05"
                      value={selectedAppearance.text_scale}
                      onChange={(event) => updateTextScaleWithApplyMode(event.target.value)}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_TEXT_SCALE}
                      max={MAX_CALLOUT_TEXT_SCALE}
                      step="0.05"
                      value={selectedAppearance.text_scale}
                      onChange={(event) => updateTextScaleWithApplyMode(event.target.value)}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  Bubble Width
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_BUBBLE_WIDTH}
                      max={MAX_CALLOUT_BUBBLE_WIDTH}
                      step="0.05"
                      value={selectedAppearance.bubble_width}
                      onChange={(event) => updateBubbleDimensionWithApplyMode('bubble_width', event.target.value)}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_BUBBLE_WIDTH}
                      max={MAX_CALLOUT_BUBBLE_WIDTH}
                      step="0.05"
                      value={selectedAppearance.bubble_width}
                      onChange={(event) => updateBubbleDimensionWithApplyMode('bubble_width', event.target.value)}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  Bubble Height
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_BUBBLE_HEIGHT}
                      max={MAX_CALLOUT_BUBBLE_HEIGHT}
                      step="0.05"
                      value={selectedAppearance.bubble_height}
                      onChange={(event) => updateBubbleDimensionWithApplyMode('bubble_height', event.target.value)}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_BUBBLE_HEIGHT}
                      max={MAX_CALLOUT_BUBBLE_HEIGHT}
                      step="0.05"
                      value={selectedAppearance.bubble_height}
                      onChange={(event) => updateBubbleDimensionWithApplyMode('bubble_height', event.target.value)}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  Bubble Radius
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_BUBBLE_RADIUS}
                      max={MAX_CALLOUT_BUBBLE_RADIUS}
                      step="0.5"
                      value={selectedAppearance.bubble_radius}
                      onChange={(event) => updateSelectedVisualSetting({
                        bubble_radius: normalizeCalloutBubbleRadius(event.target.value, selectedAppearance.bubble_radius),
                      })}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_BUBBLE_RADIUS}
                      max={MAX_CALLOUT_BUBBLE_RADIUS}
                      step="0.5"
                      value={selectedAppearance.bubble_radius}
                      onChange={(event) => updateSelectedVisualSetting({
                        bubble_radius: normalizeCalloutBubbleRadius(event.target.value, selectedAppearance.bubble_radius),
                      })}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  Bubble Outline
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_BUBBLE_STROKE_WIDTH}
                      max={MAX_CALLOUT_BUBBLE_STROKE_WIDTH}
                      step="0.1"
                      value={selectedAppearance.bubble_stroke_width}
                      onChange={(event) => updateSelectedVisualSetting({
                        bubble_stroke_width: normalizeCalloutBubbleStrokeWidth(event.target.value, selectedAppearance.bubble_stroke_width),
                      })}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_BUBBLE_STROKE_WIDTH}
                      max={MAX_CALLOUT_BUBBLE_STROKE_WIDTH}
                      step="0.1"
                      value={selectedAppearance.bubble_stroke_width}
                      onChange={(event) => updateSelectedVisualSetting({
                        bubble_stroke_width: normalizeCalloutBubbleStrokeWidth(event.target.value, selectedAppearance.bubble_stroke_width),
                      })}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  Line Thickness
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_LINE_STROKE_WIDTH}
                      max={MAX_CALLOUT_LINE_STROKE_WIDTH}
                      step="0.1"
                      value={selectedAppearance.line_stroke_width}
                      onChange={(event) => updateSelectedVisualSetting({
                        line_stroke_width: normalizeCalloutLineStrokeWidth(event.target.value, selectedAppearance.line_stroke_width),
                      })}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_LINE_STROKE_WIDTH}
                      max={MAX_CALLOUT_LINE_STROKE_WIDTH}
                      step="0.1"
                      value={selectedAppearance.line_stroke_width}
                      onChange={(event) => updateSelectedVisualSetting({
                        line_stroke_width: normalizeCalloutLineStrokeWidth(event.target.value, selectedAppearance.line_stroke_width),
                      })}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
                <FieldLabel>
                  End Bubble Size
                  <RangeFieldRow>
                    <RangeInput
                      type="range"
                      min={MIN_CALLOUT_ANCHOR_RADIUS}
                      max={MAX_CALLOUT_ANCHOR_RADIUS}
                      step="0.1"
                      value={selectedAppearance.anchor_radius}
                      onChange={(event) => updateSelectedVisualSetting({
                        anchor_radius: normalizeCalloutAnchorRadius(event.target.value, selectedAppearance.anchor_radius),
                      })}
                      disabled={disabled}
                    />
                    <FieldInput
                      type="number"
                      min={MIN_CALLOUT_ANCHOR_RADIUS}
                      max={MAX_CALLOUT_ANCHOR_RADIUS}
                      step="0.1"
                      value={selectedAppearance.anchor_radius}
                      onChange={(event) => updateSelectedVisualSetting({
                        anchor_radius: normalizeCalloutAnchorRadius(event.target.value, selectedAppearance.anchor_radius),
                      })}
                      disabled={disabled}
                    />
                  </RangeFieldRow>
                </FieldLabel>
              </InlineFields>
              <InlineFields>
                <FieldLabel>
                  Label X
                  <FieldInput
                    type="number"
                    value={selectedCallout?.label_x ?? ''}
                    onChange={(event) => updateSelectedCallout({ label_x: Number(event.target.value) })}
                    disabled={disabled}
                  />
                </FieldLabel>
                <FieldLabel>
                  Label Y
                  <FieldInput
                    type="number"
                    value={selectedCallout?.label_y ?? ''}
                    onChange={(event) => updateSelectedCallout({ label_y: Number(event.target.value) })}
                    disabled={disabled}
                  />
                </FieldLabel>
                <FieldLabel>
                  Bubble Rotation
                  <MiniActions>
                    {[0, 90, 180, 270].map((rotationValue) => (
                      <MiniButton
                        key={rotationValue}
                        type="button"
                        onClick={() => updateSelectedCallout({ rotation_deg: rotationValue })}
                        disabled={disabled}
                      >
                        {rotationValue}°
                      </MiniButton>
                    ))}
                  </MiniActions>
                </FieldLabel>
                <FieldLabel>
                  Line Attachment
                  <MiniActions>
                    {[
                      ['auto', 'Auto'],
                      ['top', 'Top'],
                      ['right', 'Right'],
                      ['bottom', 'Bottom'],
                      ['left', 'Left'],
                    ].map(([sideValue, sideLabel]) => (
                      <MiniButton
                        key={sideValue}
                        type="button"
                        onClick={() => updateSelectedCallout({ line_attach_side: sideValue })}
                        disabled={disabled}
                      >
                        {sideLabel}
                      </MiniButton>
                    ))}
                  </MiniActions>
                </FieldLabel>
                <FieldLabel $fullWidth>
                  Line Style
                  <MiniActions $singleRow>
                    {[
                      ['orthogonal', 'Orthogonal'],
                      ['diagonal45', '45° Diagonal'],
                    ].map(([styleValue, styleLabel]) => (
                      <MiniButton
                        key={styleValue}
                        type="button"
                        $active={selectedLineStyle === styleValue}
                        onClick={() => updateSelectedVisualSetting({ line_style: styleValue })}
                        disabled={disabled || (!selectedPin && !selectedCustomCallout && !selectedCallout)}
                      >
                        {styleLabel}
                      </MiniButton>
                    ))}
                  </MiniActions>
                </FieldLabel>
              </InlineFields>
              <MiniActions $center>
                {selectedRoutePointCount > 0 ? (
                  <MiniButton
                    type="button"
                    onClick={clearSelectedRoutePoints}
                    disabled={disabled || !selectedCallout}
                  >
                    <RotateCcw size={15} />
                    Reset Wire Route
                  </MiniButton>
                ) : null}
                <MiniButton
                  type="button"
                  $danger
                  onClick={() => {
                    if (selectedPin) {
                      removeCalloutForPin(selectedPin);
                      return;
                    }

                    if (selectedCustomCallout) {
                      removeCustomCallout(getCalloutLayoutKey(selectedCustomCallout));
                    }
                  }}
                  disabled={disabled || !selectedCallout}
                >
                  <X size={15} />
                  {selectedPin ? 'Return to Tray' : 'Delete Custom Callout'}
                </MiniButton>
              </MiniActions>
              <PanelText>
                Tip: drag visible labels from the tray onto the board, add a custom callout when you need a freeform label, and click a connector wire to add bend points. Blank tray gaps show which detected pins are already placed.
              </PanelText>
            </FormStack>
          )}
        </PanelCard>
      </Sidebar>
    </EditorShell>
  );
};

export default PinoutCalloutEditor;
